<template>
  <div class="leads-dashboard">
    <!-- Header Section -->
    <div class="dashboard-header">
      <div class="header-content">
        <div class="header-text">
          <h1 class="page-title">
            <i class="ri-dashboard-3-line"></i>
            דשבורד לידים
          </h1>
          <p class="page-subtitle">ניהול ומעקב אחר כל הלידים שלך במקום אחד</p>
        </div>
        <div class="header-actions">
          <button class="action-btn secondary" @click="exportLeads">
            <i class="ri-download-line"></i>
            <span>ייצוא</span>
          </button>
          <button class="action-btn primary" @click="showCreateModal = true">
            <i class="ri-add-line"></i>
            <span>ליד חדש</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Navigation Tabs -->
    <div class="tabs-section">
      <div class="tabs-container">
        <button 
          class="tab-btn"
          :class="{ active: activeTab === 'dashboard' }"
          @click="activeTab = 'dashboard'"
        >
          <i class="ri-dashboard-line"></i>
          <span>דשבורד</span>
        </button>
        <button 
          class="tab-btn"
          :class="{ active: activeTab === 'leads' }"
          @click="activeTab = 'leads'"
        >
          <i class="ri-user-star-line"></i>
          <span>רשימת לידים</span>
          <span class="tab-badge">{{ filteredLeads.length }}</span>
        </button>
      </div>
    </div>

    <!-- Dashboard Tab Content -->
    <div v-if="activeTab === 'dashboard'" class="tab-content">
      <div class="tab-header">
        <h2 class="tab-title">
          <i class="ri-dashboard-line"></i>
          <span>סקירה כללית</span>
        </h2>
        <p class="tab-subtitle">נתונים וסטטיסטיקות על הלידים שלך</p>
      </div>
      
      <!-- Modern Dashboard Cards -->
      <div class="dashboard-grid">
        <!-- Total Leads Card -->
        <div class="dashboard-card total-leads">
          <div class="card-header">
            <div class="card-icon">
              <i class="ri-user-star-line"></i>
            </div>
            <div class="card-menu">
              <i class="ri-more-2-line"></i>
            </div>
          </div>
          <div class="card-content">
            <h3 class="card-title">סה"כ לידים</h3>
            <div class="card-value">{{ totalLeads }}</div>
            <div class="card-change positive">
              <i class="ri-arrow-up-line"></i>
              <span>+12.5%</span>
              <span class="change-period">השבוע</span>
            </div>
          </div>
          <div class="card-chart">
            <svg viewBox="0 0 200 60" class="mini-chart total-chart">
              <defs>
                <linearGradient id="totalGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" style="stop-color:#60a5fa;stop-opacity:0.9"/>
                  <stop offset="50%" style="stop-color:#3b82f6;stop-opacity:0.7"/>
                  <stop offset="100%" style="stop-color:#1d4ed8;stop-opacity:0.3"/>
                </linearGradient>
                <linearGradient id="totalStroke" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" style="stop-color:#60a5fa"/>
                  <stop offset="100%" style="stop-color:#3b82f6"/>
                </linearGradient>
                <filter id="totalGlow">
                  <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                  <feDropShadow dx="0" dy="2" stdDeviation="3" flood-color="#3b82f6" flood-opacity="0.4"/>
                  <feMerge> 
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
              </defs>
              
              <!-- Background mesh -->
              <rect x="0" y="0" width="200" height="60" fill="url(#totalGradient)" opacity="0.1"/>
              
              <!-- Chart line with smooth curve -->
              <path 
                d="M10,45 C30,42 50,38 70,35 C90,32 110,28 130,25 C150,22 170,18 190,15" 
                stroke="url(#totalStroke)" 
                stroke-width="3" 
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
                filter="url(#totalGlow)"
                class="chart-path"
              >
                <animate attributeName="stroke-dasharray" values="0,300;300,0" dur="2.5s" fill="freeze"/>
              </path>
              
              <!-- Fill area -->
              <path 
                d="M10,45 C30,42 50,38 70,35 C90,32 110,28 130,25 C150,22 170,18 190,15 L190,60 L10,60 Z" 
                fill="url(#totalGradient)"
                opacity="0.6"
                class="chart-area"
              >
                <animate attributeName="opacity" values="0;0.6" dur="2s" fill="freeze"/>
              </path>
              
              <!-- Data points with pulse effect -->
              <circle cx="70" cy="35" r="3" fill="#3b82f6" class="data-dot">
                <animate attributeName="r" values="0;4;3" dur="1s" begin="1.5s" fill="freeze"/>
                <animate attributeName="opacity" values="0;1" dur="0.5s" begin="1.5s" fill="freeze"/>
              </circle>
              <circle cx="130" cy="25" r="3" fill="#1d4ed8" class="data-dot">
                <animate attributeName="r" values="0;4;3" dur="1s" begin="2s" fill="freeze"/>
                <animate attributeName="opacity" values="0;1" dur="0.5s" begin="2s" fill="freeze"/>
              </circle>
              <circle cx="190" cy="15" r="3" fill="#1e40af" class="data-dot">
                <animate attributeName="r" values="0;4;3" dur="1s" begin="2.5s" fill="freeze"/>
                <animate attributeName="opacity" values="0;1" dur="0.5s" begin="2.5s" fill="freeze"/>
              </circle>
              
              <!-- Pulse rings -->
              <circle cx="70" cy="35" r="3" fill="none" stroke="#3b82f6" stroke-width="1" opacity="0" class="pulse-ring">
                <animate attributeName="r" values="3;8;3" dur="2s" begin="3s" repeatCount="indefinite"/>
                <animate attributeName="opacity" values="0.7;0;0.7" dur="2s" begin="3s" repeatCount="indefinite"/>
              </circle>
              <circle cx="190" cy="15" r="3" fill="none" stroke="#1e40af" stroke-width="1" opacity="0" class="pulse-ring">
                <animate attributeName="r" values="3;8;3" dur="2s" begin="3.5s" repeatCount="indefinite"/>
                <animate attributeName="opacity" values="0.7;0;0.7" dur="2s" begin="3.5s" repeatCount="indefinite"/>
              </circle>
            </svg>
          </div>
        </div>

        <!-- Hot Leads Card -->
        <div class="dashboard-card hot-leads">
          <div class="card-header">
            <div class="card-icon">
              <i class="ri-fire-line"></i>
            </div>
            <div class="card-menu">
              <i class="ri-more-2-line"></i>
            </div>
          </div>
          <div class="card-content">
            <h3 class="card-title">לידים חמים</h3>
            <div class="card-value">{{ hotLeads }}</div>
            <div class="card-change positive">
              <i class="ri-arrow-up-line"></i>
              <span>+8.3%</span>
              <span class="change-period">השבוע</span>
            </div>
          </div>
          <div class="card-chart">
            <svg viewBox="0 0 200 60" class="mini-chart hot-chart">
              <defs>
                <linearGradient id="hotGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" style="stop-color:#fbbf24;stop-opacity:0.9"/>
                  <stop offset="40%" style="stop-color:#f59e0b;stop-opacity:0.8"/>
                  <stop offset="70%" style="stop-color:#ef4444;stop-opacity:0.6"/>
                  <stop offset="100%" style="stop-color:#dc2626;stop-opacity:0.3"/>
                </linearGradient>
                <linearGradient id="hotStroke" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" style="stop-color:#fbbf24"/>
                  <stop offset="100%" style="stop-color:#ef4444"/>
                </linearGradient>
                <filter id="fireGlow">
                  <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                  <feDropShadow dx="0" dy="2" stdDeviation="4" flood-color="#ef4444" flood-opacity="0.5"/>
                  <feMerge> 
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
              </defs>
              
              <!-- Fire background -->
              <rect x="0" y="0" width="200" height="60" fill="url(#hotGradient)" opacity="0.15"/>
              
              <!-- Multiple flame paths -->
              <path 
                d="M10,48 C25,45 40,42 60,38 C80,34 100,30 120,26 C140,22 160,18 190,14" 
                stroke="#fbbf24" 
                stroke-width="2" 
                fill="none"
                stroke-linecap="round"
                opacity="0.7"
                class="flame-secondary"
              >
                <animate attributeName="opacity" values="0.4;0.8;0.4" dur="3s" repeatCount="indefinite"/>
              </path>
              
              <!-- Main flame line -->
              <path 
                d="M10,50 C30,46 50,40 75,35 C100,30 125,25 150,20 C170,16 180,14 190,12" 
                stroke="url(#hotStroke)" 
                stroke-width="3" 
                fill="none"
                stroke-linecap="round"
                filter="url(#fireGlow)"
                class="chart-path flame-main"
              >
                <animate attributeName="stroke-dasharray" values="0,280;280,0" dur="2.8s" fill="freeze"/>
              </path>
              
              <!-- Fire area -->
              <path 
                d="M10,50 C30,46 50,40 75,35 C100,30 125,25 150,20 C170,16 180,14 190,12 L190,60 L10,60 Z" 
                fill="url(#hotGradient)"
                opacity="0.7"
                class="chart-area"
              >
                <animate attributeName="opacity" values="0;0.7" dur="2.2s" fill="freeze"/>
                <animateTransform attributeName="transform" type="scale" values="1,0;1,1" dur="2.2s" fill="freeze"/>
              </path>
              
              <!-- Flame particles -->
              <circle cx="60" cy="38" r="2" fill="#fbbf24" class="flame-dot">
                <animate attributeName="cy" values="38;33;38" dur="2.5s" repeatCount="indefinite"/>
                <animate attributeName="opacity" values="0.8;0.4;0.8" dur="2.5s" repeatCount="indefinite"/>
                <animate attributeName="r" values="1.5;2.5;1.5" dur="2.5s" repeatCount="indefinite"/>
              </circle>
              <circle cx="120" cy="26" r="1.8" fill="#f59e0b" class="flame-dot">
                <animate attributeName="cy" values="26;21;26" dur="3s" repeatCount="indefinite"/>
                <animate attributeName="opacity" values="0.9;0.3;0.9" dur="3s" repeatCount="indefinite"/>
                <animate attributeName="r" values="1.3;2.3;1.3" dur="3s" repeatCount="indefinite"/>
              </circle>
              <circle cx="170" cy="18" r="1.5" fill="#ef4444" class="flame-dot">
                <animate attributeName="cy" values="18;13;18" dur="2.2s" repeatCount="indefinite"/>
                <animate attributeName="opacity" values="1;0.2;1" dur="2.2s" repeatCount="indefinite"/>
                <animate attributeName="r" values="1;2;1" dur="2.2s" repeatCount="indefinite"/>
              </circle>
            </svg>
          </div>
        </div>

        <!-- Conversions Card -->
        <div class="dashboard-card conversions">
          <div class="card-header">
            <div class="card-icon">
              <i class="ri-trophy-line"></i>
            </div>
            <div class="card-menu">
              <i class="ri-more-2-line"></i>
            </div>
          </div>
          <div class="card-content">
            <h3 class="card-title">המרות</h3>
            <div class="card-value">{{ conversions }}</div>
            <div class="card-change positive">
              <i class="ri-arrow-up-line"></i>
              <span>+15.7%</span>
              <span class="change-period">השבוע</span>
            </div>
          </div>
          <div class="card-chart">
            <svg viewBox="0 0 200 60" class="mini-chart success-chart">
              <defs>
                <linearGradient id="successGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" style="stop-color:#4ade80;stop-opacity:0.9"/>
                  <stop offset="30%" style="stop-color:#22c55e;stop-opacity:0.8"/>
                  <stop offset="70%" style="stop-color:#16a34a;stop-opacity:0.6"/>
                  <stop offset="100%" style="stop-color:#15803d;stop-opacity:0.3"/>
                </linearGradient>
                <linearGradient id="successStroke" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" style="stop-color:#4ade80"/>
                  <stop offset="100%" style="stop-color:#22c55e"/>
                </linearGradient>
                <filter id="successGlow">
                  <feGaussianBlur stdDeviation="2.5" result="coloredBlur"/>
                  <feDropShadow dx="0" dy="2" stdDeviation="3" flood-color="#22c55e" flood-opacity="0.4"/>
                  <feMerge> 
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
              </defs>
              
              <!-- Success background -->
              <rect x="0" y="0" width="200" height="60" fill="url(#successGradient)" opacity="0.12"/>
              
              <!-- Success steps chart -->
              <path 
                d="M10,48 L35,44 L60,40 L85,35 L110,30 L135,25 L160,20 L190,15" 
                stroke="url(#successStroke)" 
                stroke-width="3" 
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
                filter="url(#successGlow)"
                class="chart-path"
              >
                <animate attributeName="stroke-dasharray" values="0,320;320,0" dur="3s" fill="freeze"/>
              </path>
              
              <!-- Success bars -->
              <rect x="33" y="42" width="4" height="6" fill="#4ade80" rx="2" class="success-bar">
                <animate attributeName="height" values="0;6" dur="0.6s" begin="1.2s" fill="freeze"/>
                <animate attributeName="y" values="48;42" dur="0.6s" begin="1.2s" fill="freeze"/>
              </rect>
              <rect x="58" y="38" width="4" height="8" fill="#22c55e" rx="2" class="success-bar">
                <animate attributeName="height" values="0;8" dur="0.6s" begin="1.6s" fill="freeze"/>
                <animate attributeName="y" values="46;38" dur="0.6s" begin="1.6s" fill="freeze"/>
              </rect>
              <rect x="83" y="33" width="4" height="10" fill="#16a34a" rx="2" class="success-bar">
                <animate attributeName="height" values="0;10" dur="0.6s" begin="2s" fill="freeze"/>
                <animate attributeName="y" values="43;33" dur="0.6s" begin="2s" fill="freeze"/>
              </rect>
              <rect x="133" y="23" width="4" height="12" fill="#15803d" rx="2" class="success-bar">
                <animate attributeName="height" values="0;12" dur="0.6s" begin="2.4s" fill="freeze"/>
                <animate attributeName="y" values="35;23" dur="0.6s" begin="2.4s" fill="freeze"/>
              </rect>
              
              <!-- Fill area -->
              <path 
                d="M10,48 L35,44 L60,40 L85,35 L110,30 L135,25 L160,20 L190,15 L190,60 L10,60 Z" 
                fill="url(#successGradient)"
                opacity="0.6"
                class="chart-area"
              >
                <animate attributeName="opacity" values="0;0.6" dur="2.5s" fill="freeze"/>
              </path>
              
              <!-- Success indicators -->
              <circle cx="85" cy="35" r="3" fill="#22c55e" class="success-dot">
                <animate attributeName="r" values="0;4;3" dur="1s" begin="2.5s" fill="freeze"/>
                <animate attributeName="opacity" values="0;1" dur="0.5s" begin="2.5s" fill="freeze"/>
              </circle>
              <circle cx="160" cy="20" r="3" fill="#16a34a" class="success-dot">
                <animate attributeName="r" values="0;4;3" dur="1s" begin="3s" fill="freeze"/>
                <animate attributeName="opacity" values="0;1" dur="0.5s" begin="3s" fill="freeze"/>
              </circle>
              
              <!-- Success sparkles -->
              <circle cx="110" cy="30" r="1" fill="#4ade80" opacity="0" class="sparkle">
                <animate attributeName="opacity" values="0;1;0" dur="1.5s" begin="3.5s" repeatCount="indefinite"/>
                <animate attributeName="r" values="0.5;1.5;0.5" dur="1.5s" begin="3.5s" repeatCount="indefinite"/>
              </circle>
            </svg>
          </div>
        </div>

        <!-- Revenue Card -->
        <div class="dashboard-card revenue">
          <div class="card-header">
            <div class="card-icon">
              <i class="ri-money-dollar-circle-line"></i>
            </div>
            <div class="card-menu">
              <i class="ri-more-2-line"></i>
            </div>
          </div>
          <div class="card-content">
            <h3 class="card-title">הכנסות</h3>
            <div class="card-value">₪{{ revenue.toLocaleString() }}</div>
            <div class="card-change positive">
              <i class="ri-arrow-up-line"></i>
              <span>+22.1%</span>
              <span class="change-period">השבוע</span>
            </div>
          </div>
          <div class="card-chart">
            <svg viewBox="0 0 200 60" class="mini-chart revenue-chart">
              <defs>
                <linearGradient id="revenueGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" style="stop-color:#c084fc;stop-opacity:0.9"/>
                  <stop offset="25%" style="stop-color:#a855f7;stop-opacity:0.8"/>
                  <stop offset="50%" style="stop-color:#9333ea;stop-opacity:0.7"/>
                  <stop offset="75%" style="stop-color:#7c3aed;stop-opacity:0.5"/>
                  <stop offset="100%" style="stop-color:#6b21a8;stop-opacity:0.3"/>
                </linearGradient>
                <linearGradient id="revenueStroke" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" style="stop-color:#c084fc"/>
                  <stop offset="100%" style="stop-color:#8b5cf6"/>
                </linearGradient>
                <filter id="moneyGlow">
                  <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                  <feDropShadow dx="0" dy="3" stdDeviation="4" flood-color="#8b5cf6" flood-opacity="0.4"/>
                  <feMerge> 
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
                <pattern id="moneyPattern" patternUnits="userSpaceOnUse" width="15" height="15" patternTransform="rotate(45)">
                  <rect width="7" height="15" fill="rgba(168, 85, 247, 0.08)"/>
                </pattern>
              </defs>
              
              <!-- Money pattern background -->
              <rect x="0" y="0" width="200" height="60" fill="url(#moneyPattern)"/>
              <rect x="0" y="0" width="200" height="60" fill="url(#revenueGradient)" opacity="0.1"/>
              
              <!-- Secondary flow -->
              <path 
                d="M10,52 C35,48 60,44 90,40 C120,36 150,32 180,28" 
                stroke="#c084fc" 
                stroke-width="2" 
                fill="none"
                stroke-linecap="round"
                opacity="0.6"
                class="revenue-flow-secondary"
              >
                <animate attributeName="opacity" values="0;0.7" dur="1.8s" begin="0.8s" fill="freeze"/>
              </path>
              
              <!-- Main revenue flow -->
              <path 
                d="M10,48 C40,44 70,38 100,32 C130,26 160,20 190,14" 
                stroke="url(#revenueStroke)" 
                stroke-width="4" 
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
                filter="url(#moneyGlow)"
                class="chart-path revenue-flow"
              >
                <animate attributeName="stroke-dasharray" values="0,280;280,0" dur="2.8s" fill="freeze"/>
                <animate attributeName="stroke-width" values="3;5;4" dur="4s" repeatCount="indefinite"/>
              </path>
              
              <!-- Fill area -->
              <path 
                d="M10,48 C40,44 70,38 100,32 C130,26 160,20 190,14 L190,60 L10,60 Z" 
                fill="url(#revenueGradient)"
                opacity="0.7"
                class="chart-area"
              >
                <animate attributeName="opacity" values="0;0.7" dur="2.5s" fill="freeze"/>
              </path>
              
              <!-- Money symbols -->
              <text x="45" y="42" font-family="Arial" font-size="8" fill="#9333ea" opacity="0.7" class="money-symbol">₪</text>
              <text x="85" y="36" font-family="Arial" font-size="8" fill="#7c3aed" opacity="0.7" class="money-symbol">₪</text>
              <text x="125" y="28" font-family="Arial" font-size="8" fill="#6b21a8" opacity="0.7" class="money-symbol">₪</text>
              <text x="165" y="22" font-family="Arial" font-size="8" fill="#581c87" opacity="0.7" class="money-symbol">₪</text>
              
              <!-- Revenue peaks -->
              <circle cx="100" cy="32" r="3" fill="#a855f7" class="revenue-dot">
                <animate attributeName="r" values="0;4;3" dur="1s" begin="2.5s" fill="freeze"/>
                <animate attributeName="fill" values="#a855f7;#c084fc;#a855f7" dur="3s" begin="3s" repeatCount="indefinite"/>
              </circle>
              <circle cx="160" cy="20" r="3" fill="#9333ea" class="revenue-dot">
                <animate attributeName="r" values="0;4;3" dur="1s" begin="3s" fill="freeze"/>
                <animate attributeName="fill" values="#9333ea;#a855f7;#9333ea" dur="3s" begin="3.5s" repeatCount="indefinite"/>
              </circle>
              
              <!-- Money flow particles -->
              <circle cx="70" cy="38" r="1.5" fill="#c084fc" opacity="0" class="money-particle">
                <animate attributeName="opacity" values="0;1;0" dur="2s" begin="4s" repeatCount="indefinite"/>
                <animate attributeName="cx" values="70;85;100" dur="2s" begin="4s" repeatCount="indefinite"/>
                <animate attributeName="cy" values="38;35;32" dur="2s" begin="4s" repeatCount="indefinite"/>
              </circle>
            </svg>
          </div>
        </div>

        <!-- Revenue Growth Card -->
        <div class="dashboard-card revenue-growth">
          <div class="card-header">
            <div class="card-icon">
              <i class="ri-line-chart-line"></i>
            </div>
            <div class="card-menu">
              <i class="ri-more-2-line"></i>
            </div>
          </div>
          <div class="card-content">
            <h3 class="card-title">גידול בהכנסות</h3>
            <div class="card-value">+47.2%</div>
            <div class="card-change positive">
              <i class="ri-arrow-up-line"></i>
              <span>+₪850K</span>
              <span class="change-period">השנה</span>
            </div>
          </div>
          <div class="card-chart">
            <svg viewBox="0 0 200 60" class="mini-chart growth-chart">
              <defs>
                <linearGradient id="growthGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" style="stop-color:#06d6a0;stop-opacity:0.9"/>
                  <stop offset="50%" style="stop-color:#118c4f;stop-opacity:0.7"/>
                  <stop offset="100%" style="stop-color:#2d5016;stop-opacity:0.3"/>
                </linearGradient>
                <linearGradient id="growthStroke" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" style="stop-color:#06d6a0"/>
                  <stop offset="100%" style="stop-color:#118c4f"/>
                </linearGradient>
                <filter id="growthGlow">
                  <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                  <feDropShadow dx="0" dy="3" stdDeviation="5" flood-color="#06d6a0" flood-opacity="0.5"/>
                  <feMerge> 
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
              </defs>
              
              <!-- Growth pattern background -->
              <rect x="0" y="0" width="200" height="60" fill="url(#growthGradient)" opacity="0.12"/>
              
              <!-- Exponential growth curve -->
              <path 
                d="M10,55 C25,52 40,48 55,42 C70,35 85,25 100,18 C115,12 130,8 145,6 C160,4 175,3 190,2" 
                stroke="url(#growthStroke)" 
                stroke-width="4" 
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
                filter="url(#growthGlow)"
                class="chart-path growth-line"
              >
                <animate attributeName="stroke-dasharray" values="0,350;350,0" dur="3s" fill="freeze"/>
                <animate attributeName="stroke-width" values="3;5;4" dur="4s" repeatCount="indefinite"/>
              </path>
              
              <!-- Fill area -->
              <path 
                d="M10,55 C25,52 40,48 55,42 C70,35 85,25 100,18 C115,12 130,8 145,6 C160,4 175,3 190,2 L190,60 L10,60 Z" 
                fill="url(#growthGradient)"
                opacity="0.7"
                class="chart-area"
              >
                <animate attributeName="opacity" values="0;0.7" dur="2.5s" fill="freeze"/>
              </path>
              
              <!-- Growth indicators -->
              <circle cx="55" cy="42" r="3" fill="#06d6a0" class="growth-dot">
                <animate attributeName="r" values="0;5;3" dur="1s" begin="2s" fill="freeze"/>
                <animate attributeName="opacity" values="0;1" dur="0.5s" begin="2s" fill="freeze"/>
              </circle>
              <circle cx="100" cy="18" r="3" fill="#118c4f" class="growth-dot">
                <animate attributeName="r" values="0;5;3" dur="1s" begin="2.5s" fill="freeze"/>
                <animate attributeName="opacity" values="0;1" dur="0.5s" begin="2.5s" fill="freeze"/>
              </circle>
              <circle cx="190" cy="2" r="3" fill="#2d5016" class="growth-dot">
                <animate attributeName="r" values="0;6;3" dur="1s" begin="3s" fill="freeze"/>
                <animate attributeName="opacity" values="0;1" dur="0.5s" begin="3s" fill="freeze"/>
              </circle>
              
              <!-- Growth arrows -->
              <path d="M180,12 L190,2 L185,8" stroke="#118c4f" stroke-width="2" fill="none" opacity="0" class="growth-arrow">
                <animate attributeName="opacity" values="0;1" dur="0.5s" begin="3.5s" fill="freeze"/>
              </path>
              
              <!-- Success sparkles -->
              <circle cx="130" cy="8" r="1" fill="#06d6a0" opacity="0" class="sparkle">
                <animate attributeName="opacity" values="0;1;0" dur="2s" begin="4s" repeatCount="indefinite"/>
                <animate attributeName="r" values="0.5;2;0.5" dur="2s" begin="4s" repeatCount="indefinite"/>
              </circle>
              <circle cx="165" cy="4" r="1" fill="#118c4f" opacity="0" class="sparkle">
                <animate attributeName="opacity" values="0;1;0" dur="2s" begin="4.5s" repeatCount="indefinite"/>
                <animate attributeName="r" values="0.5;2;0.5" dur="2s" begin="4.5s" repeatCount="indefinite"/>
              </circle>
            </svg>
          </div>
        </div>

        <!-- Lead Conversion Card -->
        <div class="dashboard-card lead-conversion">
          <div class="card-header">
            <div class="card-icon">
              <i class="ri-exchange-line"></i>
            </div>
            <div class="card-menu">
              <i class="ri-more-2-line"></i>
            </div>
          </div>
          <div class="card-content">
            <h3 class="card-title">שיעור המרה</h3>
            <div class="card-value">73.5%</div>
            <div class="card-change positive">
              <i class="ri-arrow-up-line"></i>
              <span>+5.8%</span>
              <span class="change-period">החודש</span>
            </div>
          </div>
          <div class="card-chart">
            <svg viewBox="0 0 200 60" class="mini-chart conversion-chart">
              <defs>
                <linearGradient id="conversionGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" style="stop-color:#ff6b6b;stop-opacity:0.9"/>
                  <stop offset="30%" style="stop-color:#ffa500;stop-opacity:0.8"/>
                  <stop offset="70%" style="stop-color:#32cd32;stop-opacity:0.7"/>
                  <stop offset="100%" style="stop-color:#228b22;stop-opacity:0.3"/>
                </linearGradient>
                <linearGradient id="conversionStroke" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" style="stop-color:#ff6b6b"/>
                  <stop offset="50%" style="stop-color:#ffa500"/>
                  <stop offset="100%" style="stop-color:#32cd32"/>
                </linearGradient>
                <filter id="conversionGlow">
                  <feGaussianBlur stdDeviation="2.5" result="coloredBlur"/>
                  <feDropShadow dx="0" dy="2" stdDeviation="4" flood-color="#32cd32" flood-opacity="0.4"/>
                  <feMerge> 
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
              </defs>
              
              <!-- Conversion funnel background -->
              <rect x="0" y="0" width="200" height="60" fill="url(#conversionGradient)" opacity="0.1"/>
              
              <!-- Conversion funnel steps -->
              <rect x="10" y="45" width="30" height="10" fill="#ff6b6b" rx="2" class="funnel-step">
                <animate attributeName="height" values="0;10" dur="0.8s" begin="1s" fill="freeze"/>
                <animate attributeName="y" values="55;45" dur="0.8s" begin="1s" fill="freeze"/>
              </rect>
              <rect x="50" y="40" width="25" height="15" fill="#ffa500" rx="2" class="funnel-step">
                <animate attributeName="height" values="0;15" dur="0.8s" begin="1.3s" fill="freeze"/>
                <animate attributeName="y" values="55;40" dur="0.8s" begin="1.3s" fill="freeze"/>
              </rect>
              <rect x="85" y="35" width="20" height="20" fill="#ffcc00" rx="2" class="funnel-step">
                <animate attributeName="height" values="0;20" dur="0.8s" begin="1.6s" fill="freeze"/>
                <animate attributeName="y" values="55;35" dur="0.8s" begin="1.6s" fill="freeze"/>
              </rect>
              <rect x="115" y="25" width="15" height="30" fill="#32cd32" rx="2" class="funnel-step">
                <animate attributeName="height" values="0;30" dur="0.8s" begin="1.9s" fill="freeze"/>
                <animate attributeName="y" values="55;25" dur="0.8s" begin="1.9s" fill="freeze"/>
              </rect>
              
              <!-- Conversion flow line -->
              <path 
                d="M25,45 C35,42 45,38 55,35 C65,32 75,28 85,25 C95,22 105,18 115,15 C125,12 135,10 145,8 C155,6 165,5 185,4" 
                stroke="url(#conversionStroke)" 
                stroke-width="3" 
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
                filter="url(#conversionGlow)"
                class="chart-path conversion-line"
              >
                <animate attributeName="stroke-dasharray" values="0,300;300,0" dur="2.5s" fill="freeze"/>
              </path>
              
              <!-- Conversion indicators -->
              <circle cx="25" cy="45" r="2" fill="#ff6b6b" class="conversion-dot">
                <animate attributeName="r" values="0;3;2" dur="0.5s" begin="2.5s" fill="freeze"/>
              </circle>
              <circle cx="87" cy="35" r="2" fill="#ffcc00" class="conversion-dot">
                <animate attributeName="r" values="0;3;2" dur="0.5s" begin="3s" fill="freeze"/>
              </circle>
              <circle cx="185" cy="4" r="2" fill="#32cd32" class="conversion-dot">
                <animate attributeName="r" values="0;4;2" dur="0.5s" begin="3.5s" fill="freeze"/>
              </circle>
              
              <!-- Percentage indicators -->
              <text x="25" y="40" font-family="Arial" font-size="6" fill="#ff6b6b" opacity="0" class="percentage-label">
                100%
                <animate attributeName="opacity" values="0;1" dur="0.5s" begin="2.5s" fill="freeze"/>
              </text>
              <text x="82" y="30" font-family="Arial" font-size="6" fill="#ffcc00" opacity="0" class="percentage-label">
                85%
                <animate attributeName="opacity" values="0;1" dur="0.5s" begin="3s" fill="freeze"/>
              </text>
              <text x="175" y="0" font-family="Arial" font-size="6" fill="#32cd32" opacity="0" class="percentage-label">
                73.5%
                <animate attributeName="opacity" values="0;1" dur="0.5s" begin="3.5s" fill="freeze"/>
              </text>
            </svg>
          </div>
        </div>

        <!-- Total Orders Card -->
        <div class="dashboard-card total-orders">
          <div class="card-header">
            <div class="card-icon">
              <i class="ri-shopping-cart-line"></i>
            </div>
            <div class="card-menu">
              <i class="ri-more-2-line"></i>
            </div>
          </div>
          <div class="card-content">
            <h3 class="card-title">סה״כ הזמנות</h3>
            <div class="card-value">2,847</div>
            <div class="card-change positive">
              <i class="ri-arrow-up-line"></i>
              <span>+156</span>
              <span class="change-period">השבוע</span>
            </div>
          </div>
          <div class="card-chart">
            <svg viewBox="0 0 200 60" class="mini-chart orders-chart">
              <defs>
                <linearGradient id="ordersGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" style="stop-color:#667eea;stop-opacity:0.9"/>
                  <stop offset="50%" style="stop-color:#764ba2;stop-opacity:0.7"/>
                  <stop offset="100%" style="stop-color:#f093fb;stop-opacity:0.3"/>
                </linearGradient>
                <linearGradient id="ordersStroke" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" style="stop-color:#667eea"/>
                  <stop offset="100%" style="stop-color:#764ba2"/>
                </linearGradient>
                <filter id="ordersGlow">
                  <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                  <feDropShadow dx="0" dy="2" stdDeviation="3" flood-color="#667eea" flood-opacity="0.4"/>
                  <feMerge> 
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
              </defs>
              
              <!-- Orders background -->
              <rect x="0" y="0" width="200" height="60" fill="url(#ordersGradient)" opacity="0.1"/>
              
              <!-- Order bars -->
              <rect x="15" y="40" width="8" height="15" fill="#667eea" rx="2" class="order-bar">
                <animate attributeName="height" values="0;15" dur="0.6s" begin="0.5s" fill="freeze"/>
                <animate attributeName="y" values="55;40" dur="0.6s" begin="0.5s" fill="freeze"/>
              </rect>
              <rect x="30" y="35" width="8" height="20" fill="#667eea" rx="2" class="order-bar">
                <animate attributeName="height" values="0;20" dur="0.6s" begin="0.8s" fill="freeze"/>
                <animate attributeName="y" values="55;35" dur="0.6s" begin="0.8s" fill="freeze"/>
              </rect>
              <rect x="45" y="30" width="8" height="25" fill="#6a7cf7" rx="2" class="order-bar">
                <animate attributeName="height" values="0;25" dur="0.6s" begin="1.1s" fill="freeze"/>
                <animate attributeName="y" values="55;30" dur="0.6s" begin="1.1s" fill="freeze"/>
              </rect>
              <rect x="60" y="25" width="8" height="30" fill="#6a7cf7" rx="2" class="order-bar">
                <animate attributeName="height" values="0;30" dur="0.6s" begin="1.4s" fill="freeze"/>
                <animate attributeName="y" values="55;25" dur="0.6s" begin="1.4s" fill="freeze"/>
              </rect>
              <rect x="75" y="20" width="8" height="35" fill="#764ba2" rx="2" class="order-bar">
                <animate attributeName="height" values="0;35" dur="0.6s" begin="1.7s" fill="freeze"/>
                <animate attributeName="y" values="55;20" dur="0.6s" begin="1.7s" fill="freeze"/>
              </rect>
              <rect x="90" y="15" width="8" height="40" fill="#764ba2" rx="2" class="order-bar">
                <animate attributeName="height" values="0;40" dur="0.6s" begin="2s" fill="freeze"/>
                <animate attributeName="y" values="55;15" dur="0.6s" begin="2s" fill="freeze"/>
              </rect>
              <rect x="105" y="18" width="8" height="37" fill="#764ba2" rx="2" class="order-bar">
                <animate attributeName="height" values="0;37" dur="0.6s" begin="2.3s" fill="freeze"/>
                <animate attributeName="y" values="55;18" dur="0.6s" begin="2.3s" fill="freeze"/>
              </rect>
              <rect x="120" y="12" width="8" height="43" fill="#8a2be2" rx="2" class="order-bar">
                <animate attributeName="height" values="0;43" dur="0.6s" begin="2.6s" fill="freeze"/>
                <animate attributeName="y" values="55;12" dur="0.6s" begin="2.6s" fill="freeze"/>
              </rect>
              <rect x="135" y="8" width="8" height="47" fill="#8a2be2" rx="2" class="order-bar">
                <animate attributeName="height" values="0;47" dur="0.6s" begin="2.9s" fill="freeze"/>
                <animate attributeName="y" values="55;8" dur="0.6s" begin="2.9s" fill="freeze"/>
              </rect>
              <rect x="150" y="10" width="8" height="45" fill="#9a4ef0" rx="2" class="order-bar">
                <animate attributeName="height" values="0;45" dur="0.6s" begin="3.2s" fill="freeze"/>
                <animate attributeName="y" values="55;10" dur="0.6s" begin="3.2s" fill="freeze"/>
              </rect>
              <rect x="165" y="6" width="8" height="49" fill="#9a4ef0" rx="2" class="order-bar">
                <animate attributeName="height" values="0;49" dur="0.6s" begin="3.5s" fill="freeze"/>
                <animate attributeName="y" values="55;6" dur="0.6s" begin="3.5s" fill="freeze"/>
              </rect>
              <rect x="180" y="4" width="8" height="51" fill="#ab5fec" rx="2" class="order-bar">
                <animate attributeName="height" values="0;51" dur="0.6s" begin="3.8s" fill="freeze"/>
                <animate attributeName="y" values="55;4" dur="0.6s" begin="3.8s" fill="freeze"/>
              </rect>
              
              <!-- Trend line -->
              <path 
                d="M19,47 L34,42 L49,37 L64,32 L79,27 L94,22 L109,24 L124,19 L139,15 L154,17 L169,13 L184,11" 
                stroke="url(#ordersStroke)" 
                stroke-width="2" 
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
                filter="url(#ordersGlow)"
                class="chart-path orders-line"
              >
                <animate attributeName="stroke-dasharray" values="0,250;250,0" dur="4s" fill="freeze"/>
              </path>
              
              <!-- Order package icons -->
              <rect x="170" y="10" width="4" height="4" fill="#ab5fec" rx="1" opacity="0" class="package-icon">
                <animate attributeName="opacity" values="0;1" dur="0.3s" begin="4s" fill="freeze"/>
              </rect>
              <rect x="180" y="8" width="4" height="4" fill="#ab5fec" rx="1" opacity="0" class="package-icon">
                <animate attributeName="opacity" values="0;1" dur="0.3s" begin="4.2s" fill="freeze"/>
              </rect>
            </svg>
          </div>
        </div>

        <!-- Annual Profit Card -->
        <div class="dashboard-card annual-profit">
          <div class="card-header">
            <div class="card-icon">
              <i class="ri-trophy-line"></i>
            </div>
            <div class="card-menu">
              <i class="ri-more-2-line"></i>
            </div>
          </div>
          <div class="card-content">
            <h3 class="card-title">רווח שנתי</h3>
            <div class="card-value">₪1.2M</div>
            <div class="card-change positive">
              <i class="ri-arrow-up-line"></i>
              <span>+28.5%</span>
              <span class="change-period">לעומת אשתקד</span>
            </div>
          </div>
          <div class="card-chart">
            <svg viewBox="0 0 200 60" class="mini-chart profit-chart">
              <defs>
                <linearGradient id="profitGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" style="stop-color:#ffd700;stop-opacity:0.9"/>
                  <stop offset="30%" style="stop-color:#ffb347;stop-opacity:0.8"/>
                  <stop offset="70%" style="stop-color:#ff8c00;stop-opacity:0.6"/>
                  <stop offset="100%" style="stop-color:#ff6347;stop-opacity:0.3"/>
                </linearGradient>
                <linearGradient id="profitStroke" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" style="stop-color:#ffd700"/>
                  <stop offset="100%" style="stop-color:#ff8c00"/>
                </linearGradient>
                <filter id="profitGlow">
                  <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                  <feDropShadow dx="0" dy="3" stdDeviation="5" flood-color="#ffd700" flood-opacity="0.5"/>
                  <feMerge> 
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
                <pattern id="goldPattern" patternUnits="userSpaceOnUse" width="12" height="12" patternTransform="rotate(45)">
                  <rect width="6" height="12" fill="rgba(255, 215, 0, 0.1)"/>
                </pattern>
              </defs>
              
              <!-- Gold pattern background -->
              <rect x="0" y="0" width="200" height="60" fill="url(#goldPattern)"/>
              <rect x="0" y="0" width="200" height="60" fill="url(#profitGradient)" opacity="0.15"/>
              
              <!-- Profit mountains -->
              <path 
                d="M10,55 L25,45 L40,35 L55,25 L70,15 L85,10 L100,8 L115,5 L130,4 L145,3 L160,2 L175,1 L190,0" 
                stroke="url(#profitStroke)" 
                stroke-width="4" 
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
                filter="url(#profitGlow)"
                class="chart-path profit-line"
              >
                <animate attributeName="stroke-dasharray" values="0,400;400,0" dur="3.5s" fill="freeze"/>
                <animate attributeName="stroke-width" values="3;6;4" dur="5s" repeatCount="indefinite"/>
              </path>
              
              <!-- Fill area -->
              <path 
                d="M10,55 L25,45 L40,35 L55,25 L70,15 L85,10 L100,8 L115,5 L130,4 L145,3 L160,2 L175,1 L190,0 L190,60 L10,60 Z" 
                fill="url(#profitGradient)"
                opacity="0.8"
                class="chart-area"
              >
                <animate attributeName="opacity" values="0;0.8" dur="3s" fill="freeze"/>
              </path>
              
              <!-- Profit peaks -->
              <circle cx="70" cy="15" r="3" fill="#ffd700" class="profit-dot">
                <animate attributeName="r" values="0;5;3" dur="1s" begin="3s" fill="freeze"/>
                <animate attributeName="opacity" values="0;1" dur="0.5s" begin="3s" fill="freeze"/>
              </circle>
              <circle cx="130" cy="4" r="3" fill="#ffb347" class="profit-dot">
                <animate attributeName="r" values="0;5;3" dur="1s" begin="3.5s" fill="freeze"/>
                <animate attributeName="opacity" values="0;1" dur="0.5s" begin="3.5s" fill="freeze"/>
              </circle>
              <circle cx="190" cy="0" r="3" fill="#ff8c00" class="profit-dot">
                <animate attributeName="r" values="0;6;3" dur="1s" begin="4s" fill="freeze"/>
                <animate attributeName="opacity" values="0;1" dur="0.5s" begin="4s" fill="freeze"/>
              </circle>
              
              <!-- Crown/Trophy icons -->
              <path d="M185,5 L190,0 L195,5 L192,8 L188,8 Z" fill="#ffd700" opacity="0" class="crown">
                <animate attributeName="opacity" values="0;1" dur="0.5s" begin="4.5s" fill="freeze"/>
              </path>
              
              <!-- Gold sparkles -->
              <circle cx="85" cy="10" r="1" fill="#ffd700" opacity="0" class="sparkle">
                <animate attributeName="opacity" values="0;1;0" dur="2s" begin="5s" repeatCount="indefinite"/>
                <animate attributeName="r" values="0.5;2;0.5" dur="2s" begin="5s" repeatCount="indefinite"/>
              </circle>
              <circle cx="145" cy="3" r="1" fill="#ffb347" opacity="0" class="sparkle">
                <animate attributeName="opacity" values="0;1;0" dur="2s" begin="5.5s" repeatCount="indefinite"/>
                <animate attributeName="r" values="0.5;2;0.5" dur="2s" begin="5.5s" repeatCount="indefinite"/>
              </circle>
              <circle cx="175" cy="1" r="1" fill="#ff8c00" opacity="0" class="sparkle">
                <animate attributeName="opacity" values="0;1;0" dur="2s" begin="6s" repeatCount="indefinite"/>
                <animate attributeName="r" values="0.5;2;0.5" dur="2s" begin="6s" repeatCount="indefinite"/>
              </circle>
            </svg>
          </div>
        </div>
      </div>

      <!-- Pipeline Status Section -->
      <div class="pipeline-section">
        <div class="section-header">
          <h2 class="section-title">
            <i class="ri-flow-chart"></i>
            <span>צינור מכירות</span>
          </h2>
          <div class="time-filter">
            <select v-model="timeFilter" class="filter-select" @change="applyTimeFilter">
              <option value="7">7 ימים אחרונים</option>
              <option value="30">30 ימים אחרונים</option>
              <option value="90">90 ימים אחרונים</option>
              <option value="360">שנה אחרונה</option>
            </select>
          </div>
        </div>
        
        <div class="pipeline-cards">
          <div 
            class="pipeline-card new-lead"
            :class="{ active: statusFilter === 'new_lead' }"
            @click="toggleStatusFilter('new_lead')"
          >
            <div class="pipeline-icon">
              <i class="ri-user-add-line"></i>
            </div>
            <div class="pipeline-content">
              <h4 class="pipeline-title">לידים חדשים</h4>
              <div class="pipeline-value">{{ getStatusCount('new_lead') }}</div>
              <div class="pipeline-change positive">
                <i class="ri-trending-up-line"></i>
                <span>+15%</span>
              </div>
            </div>
          </div>

          <div 
            class="pipeline-card call-scheduled"
            :class="{ active: statusFilter === 'call_scheduled' }"
            @click="toggleStatusFilter('call_scheduled')"
          >
            <div class="pipeline-icon">
              <i class="ri-phone-line"></i>
            </div>
            <div class="pipeline-content">
              <h4 class="pipeline-title">שיחות מתוזמנות</h4>
              <div class="pipeline-value">{{ getStatusCount('call_scheduled') }}</div>
              <div class="pipeline-change positive">
                <i class="ri-trending-up-line"></i>
                <span>+8%</span>
              </div>
            </div>
          </div>

          <div 
            class="pipeline-card quote-received"
            :class="{ active: statusFilter === 'quote_received' }"
            @click="toggleStatusFilter('quote_received')"
          >
            <div class="pipeline-icon">
              <i class="ri-file-text-line"></i>
            </div>
            <div class="pipeline-content">
              <h4 class="pipeline-title">הצעות מחיר</h4>
              <div class="pipeline-value">{{ getStatusCount('quote_received') }}</div>
              <div class="pipeline-change negative">
                <i class="ri-trending-down-line"></i>
                <span>-3%</span>
              </div>
            </div>
          </div>

          <div 
            class="pipeline-card deal-closed"
            :class="{ active: statusFilter === 'deal_closed' }"
            @click="toggleStatusFilter('deal_closed')"
          >
            <div class="pipeline-icon">
              <i class="ri-trophy-line"></i>
            </div>
            <div class="pipeline-content">
              <h4 class="pipeline-title">עסקאות סגורות</h4>
              <div class="pipeline-value">{{ getStatusCount('deal_closed') }}</div>
              <div class="pipeline-change positive">
                <i class="ri-trending-up-line"></i>
                <span>+23%</span>
              </div>
            </div>
          </div>

          <div 
            class="pipeline-card followup"
            :class="{ active: statusFilter === 'followup' }"
            @click="toggleStatusFilter('followup')"
          >
            <div class="pipeline-icon">
              <i class="ri-time-line"></i>
            </div>
            <div class="pipeline-content">
              <h4 class="pipeline-title">מעקב</h4>
              <div class="pipeline-value">{{ getStatusCount('followup') }}</div>
              <div class="pipeline-change neutral">
                <i class="ri-subtract-line"></i>
                <span>0%</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Advanced Analytics Section -->
      <div class="analytics-section">
        <div class="section-header">
          <h2 class="section-title">
            <i class="ri-pie-chart-line"></i>
            <span>ניתוח התפלגות לידים</span>
          </h2>
          <div class="analytics-controls">
            <button class="analytics-btn" :class="{ active: analyticsView === 'pie' }" @click="analyticsView = 'pie'">
              <i class="ri-pie-chart-line"></i>
              <span>עוגה</span>
            </button>
            <button class="analytics-btn" :class="{ active: analyticsView === 'bar' }" @click="analyticsView = 'bar'">
              <i class="ri-bar-chart-line"></i>
              <span>עמודות</span>
            </button>
          </div>
        </div>

        <div class="analytics-container">
          <div class="analytics-chart">
            <!-- Pie Chart -->
            <div v-if="analyticsView === 'pie'" class="pie-chart-container">
              <svg viewBox="0 0 280 280" class="pie-chart">
                <defs>
                  <filter id="pieGlow">
                    <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
                    <feDropShadow dx="0" dy="4" stdDeviation="8" flood-color="rgba(139, 92, 246, 0.3)"/>
                    <feMerge> 
                      <feMergeNode in="coloredBlur"/>
                      <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                  </filter>
                  
                  <!-- Enhanced gradients for pie segments -->
                  <linearGradient id="newLeadPieGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style="stop-color:#60a5fa"/>
                    <stop offset="50%" style="stop-color:#3b82f6"/>
                    <stop offset="100%" style="stop-color:#1d4ed8"/>
                  </linearGradient>
                  <linearGradient id="callScheduledPieGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style="stop-color:#a78bfa"/>
                    <stop offset="50%" style="stop-color:#8b5cf6"/>
                    <stop offset="100%" style="stop-color:#7c3aed"/>
                  </linearGradient>
                  <linearGradient id="quotePieGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style="stop-color:#fbbf24"/>
                    <stop offset="50%" style="stop-color:#f59e0b"/>
                    <stop offset="100%" style="stop-color:#d97706"/>
                  </linearGradient>
                  <linearGradient id="closedPieGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style="stop-color:#4ade80"/>
                    <stop offset="50%" style="stop-color:#22c55e"/>
                    <stop offset="100%" style="stop-color:#16a34a"/>
                  </linearGradient>
                  <linearGradient id="followupPieGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style="stop-color:#fb7185"/>
                    <stop offset="50%" style="stop-color:#f43f5e"/>
                    <stop offset="100%" style="stop-color:#e11d48"/>
                  </linearGradient>
                  
                  <!-- Inner shadow for depth -->
                  <filter id="innerShadow">
                    <feOffset dx="0" dy="2"/>
                    <feGaussianBlur stdDeviation="2" result="offset-blur"/>
                    <feFlood flood-color="#000000" flood-opacity="0.15"/>
                    <feComposite in2="offset-blur" operator="in"/>
                    <feMerge>
                      <feMergeNode/>
                      <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                  </filter>
                </defs>
                
                <!-- Background circle with subtle glow -->
                <circle cx="140" cy="140" r="110" fill="var(--color-bg-primary)" stroke="rgba(139, 92, 246, 0.1)" stroke-width="2" filter="url(#pieGlow)"/>
                
                <!-- Central circle for 3D effect -->
                <circle cx="140" cy="140" r="65" fill="var(--color-bg-secondary)" stroke="rgba(255,255,255,0.2)" stroke-width="1"/>
                
                <!-- Pie segments with enhanced styling -->
                <path 
                  :d="getPieSegmentPath(0, getStatusPercentage('new_lead'))" 
                  fill="url(#newLeadPieGradient)"
                  stroke="var(--color-bg-primary)"
                  stroke-width="3"
                  filter="url(#innerShadow)"
                  class="pie-segment enhanced"
                  :class="{ active: hoveredSegment === 'new_lead' }"
                  @mouseenter="hoveredSegment = 'new_lead'"
                  @mouseleave="hoveredSegment = null"
                >
                  <animate attributeName="opacity" values="0;1" dur="1.2s" fill="freeze"/>
                  <animateTransform attributeName="transform" type="scale" values="0;1" dur="1.2s" fill="freeze"/>
                </path>
                
                <path 
                  :d="getPieSegmentPath(getStatusPercentage('new_lead'), getStatusPercentage('call_scheduled'))" 
                  fill="url(#callScheduledPieGradient)"
                  stroke="var(--color-bg-primary)"
                  stroke-width="3"
                  filter="url(#innerShadow)"
                  class="pie-segment enhanced"
                  :class="{ active: hoveredSegment === 'call_scheduled' }"
                  @mouseenter="hoveredSegment = 'call_scheduled'"
                  @mouseleave="hoveredSegment = null"
                >
                  <animate attributeName="opacity" values="0;1" dur="1.2s" begin="0.3s" fill="freeze"/>
                  <animateTransform attributeName="transform" type="scale" values="0;1" dur="1.2s" begin="0.3s" fill="freeze"/>
                </path>
                
                <path 
                  :d="getPieSegmentPath(getStatusPercentage('new_lead') + getStatusPercentage('call_scheduled'), getStatusPercentage('quote_received'))" 
                  fill="url(#quotePieGradient)"
                  stroke="var(--color-bg-primary)"
                  stroke-width="3"
                  filter="url(#innerShadow)"
                  class="pie-segment enhanced"
                  :class="{ active: hoveredSegment === 'quote_received' }"
                  @mouseenter="hoveredSegment = 'quote_received'"
                  @mouseleave="hoveredSegment = null"
                >
                  <animate attributeName="opacity" values="0;1" dur="1.2s" begin="0.6s" fill="freeze"/>
                  <animateTransform attributeName="transform" type="scale" values="0;1" dur="1.2s" begin="0.6s" fill="freeze"/>
                </path>
                
                <path 
                  :d="getPieSegmentPath(getStatusPercentage('new_lead') + getStatusPercentage('call_scheduled') + getStatusPercentage('quote_received'), getStatusPercentage('deal_closed'))" 
                  fill="url(#closedPieGradient)"
                  stroke="var(--color-bg-primary)"
                  stroke-width="3"
                  filter="url(#innerShadow)"
                  class="pie-segment enhanced"
                  :class="{ active: hoveredSegment === 'deal_closed' }"
                  @mouseenter="hoveredSegment = 'deal_closed'"
                  @mouseleave="hoveredSegment = null"
                >
                  <animate attributeName="opacity" values="0;1" dur="1.2s" begin="0.9s" fill="freeze"/>
                  <animateTransform attributeName="transform" type="scale" values="0;1" dur="1.2s" begin="0.9s" fill="freeze"/>
                </path>
                
                <path 
                  :d="getPieSegmentPath(getStatusPercentage('new_lead') + getStatusPercentage('call_scheduled') + getStatusPercentage('quote_received') + getStatusPercentage('deal_closed'), getStatusPercentage('followup'))" 
                  fill="url(#followupPieGradient)"
                  stroke="var(--color-bg-primary)"
                  stroke-width="3"
                  filter="url(#innerShadow)"
                  class="pie-segment enhanced"
                  :class="{ active: hoveredSegment === 'followup' }"
                  @mouseenter="hoveredSegment = 'followup'"
                  @mouseleave="hoveredSegment = null"
                >
                  <animate attributeName="opacity" values="0;1" dur="1.2s" begin="1.2s" fill="freeze"/>
                  <animateTransform attributeName="transform" type="scale" values="0;1" dur="1.2s" begin="1.2s" fill="freeze"/>
                </path>

                <!-- Center content with enhanced styling -->
                <circle cx="140" cy="140" r="60" fill="linear-gradient(135deg, var(--color-bg-primary), var(--color-bg-secondary))" stroke="rgba(139, 92, 246, 0.2)" stroke-width="2"/>
                <text x="140" y="125" text-anchor="middle" class="pie-center-title enhanced">סה"כ לידים</text>
                <text x="140" y="145" text-anchor="middle" class="pie-center-value enhanced">{{ totalLeads }}</text>
                <text x="140" y="162" text-anchor="middle" class="pie-center-subtitle">פעילים במערכת</text>
                
                <!-- Decorative elements -->
                <circle cx="140" cy="140" r="50" fill="none" stroke="url(#newLeadPieGradient)" stroke-width="1" opacity="0.3">
                  <animate attributeName="r" values="45;55;45" dur="4s" repeatCount="indefinite"/>
                  <animate attributeName="opacity" values="0.1;0.4;0.1" dur="4s" repeatCount="indefinite"/>
                </circle>
              </svg>
            </div>

            <!-- Bar Chart -->
            <div v-else class="bar-chart-container">
              <svg viewBox="0 0 500 300" class="bar-chart enhanced">
                <defs>
                  <!-- Enhanced gradients for bars -->
                  <linearGradient id="barGradient1Enhanced" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" style="stop-color:#60a5fa;stop-opacity:1"/>
                    <stop offset="50%" style="stop-color:#3b82f6;stop-opacity:0.9"/>
                    <stop offset="100%" style="stop-color:#1d4ed8;stop-opacity:0.7"/>
                  </linearGradient>
                  <linearGradient id="barGradient2Enhanced" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" style="stop-color:#a78bfa;stop-opacity:1"/>
                    <stop offset="50%" style="stop-color:#8b5cf6;stop-opacity:0.9"/>
                    <stop offset="100%" style="stop-color:#7c3aed;stop-opacity:0.7"/>
                  </linearGradient>
                  <linearGradient id="barGradient3Enhanced" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" style="stop-color:#fbbf24;stop-opacity:1"/>
                    <stop offset="50%" style="stop-color:#f59e0b;stop-opacity:0.9"/>
                    <stop offset="100%" style="stop-color:#d97706;stop-opacity:0.7"/>
                  </linearGradient>
                  <linearGradient id="barGradient4Enhanced" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" style="stop-color:#4ade80;stop-opacity:1"/>
                    <stop offset="50%" style="stop-color:#22c55e;stop-opacity:0.9"/>
                    <stop offset="100%" style="stop-color:#16a34a;stop-opacity:0.7"/>
                  </linearGradient>
                  <linearGradient id="barGradient5Enhanced" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" style="stop-color:#fb7185;stop-opacity:1"/>
                    <stop offset="50%" style="stop-color:#f43f5e;stop-opacity:0.9"/>
                    <stop offset="100%" style="stop-color:#e11d48;stop-opacity:0.7"/>
                  </linearGradient>
                  
                  <!-- Shadow filter -->
                  <filter id="barShadow">
                    <feDropShadow dx="0" dy="4" stdDeviation="6" flood-color="rgba(0,0,0,0.15)"/>
                  </filter>
                  
                  <!-- Glow filter -->
                  <filter id="barGlow">
                    <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                    <feMerge>
                      <feMergeNode in="coloredBlur"/>
                      <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                  </filter>
                </defs>

                <!-- Enhanced grid with subtle styling -->
                <g class="grid-lines enhanced">
                  <line x1="60" y1="50" x2="450" y2="50" stroke="rgba(139, 92, 246, 0.15)" stroke-width="1"/>
                  <line x1="60" y1="100" x2="450" y2="100" stroke="rgba(139, 92, 246, 0.15)" stroke-width="1"/>
                  <line x1="60" y1="150" x2="450" y2="150" stroke="rgba(139, 92, 246, 0.15)" stroke-width="1"/>
                  <line x1="60" y1="200" x2="450" y2="200" stroke="rgba(139, 92, 246, 0.15)" stroke-width="1"/>
                  <line x1="60" y1="250" x2="450" y2="250" stroke="rgba(139, 92, 246, 0.2)" stroke-width="2"/>
                </g>

                <!-- Y-axis labels -->
                <g class="y-axis-labels">
                  <text x="50" y="55" text-anchor="end" class="axis-label">{{ Math.max(...['new_lead', 'call_scheduled', 'quote_received', 'deal_closed', 'followup'].map(s => getStatusCount(s))) }}</text>
                  <text x="50" y="105" text-anchor="end" class="axis-label">{{ Math.round(Math.max(...['new_lead', 'call_scheduled', 'quote_received', 'deal_closed', 'followup'].map(s => getStatusCount(s))) * 0.75) }}</text>
                  <text x="50" y="155" text-anchor="end" class="axis-label">{{ Math.round(Math.max(...['new_lead', 'call_scheduled', 'quote_received', 'deal_closed', 'followup'].map(s => getStatusCount(s))) * 0.5) }}</text>
                  <text x="50" y="205" text-anchor="end" class="axis-label">{{ Math.round(Math.max(...['new_lead', 'call_scheduled', 'quote_received', 'deal_closed', 'followup'].map(s => getStatusCount(s))) * 0.25) }}</text>
                  <text x="50" y="255" text-anchor="end" class="axis-label">0</text>
                </g>

                <!-- Enhanced bars with 3D effect -->
                <g class="bars-container">
                  <!-- Bar 1 - New Leads -->
                  <rect x="80" :y="250 - getBarHeight('new_lead')" width="60" :height="getBarHeight('new_lead')" 
                        fill="url(#barGradient1Enhanced)" rx="8" 
                        filter="url(#barShadow)"
                        class="bar-segment enhanced">
                    <animate attributeName="height" :values="`0;${getBarHeight('new_lead')}`" dur="1.5s" fill="freeze"/>
                    <animate attributeName="y" :values="`250;${250 - getBarHeight('new_lead')}`" dur="1.5s" fill="freeze"/>
                  </rect>
                  <!-- 3D top for bar 1 -->
                  <ellipse :cx="110" :cy="250 - getBarHeight('new_lead')" rx="30" ry="4" fill="#60a5fa" opacity="0.8" class="bar-top">
                    <animate attributeName="opacity" values="0;0.8" dur="0.5s" begin="1.5s" fill="freeze"/>
                  </ellipse>
                  
                  <!-- Bar 2 - Call Scheduled -->
                  <rect x="160" :y="250 - getBarHeight('call_scheduled')" width="60" :height="getBarHeight('call_scheduled')" 
                        fill="url(#barGradient2Enhanced)" rx="8" 
                        filter="url(#barShadow)"
                        class="bar-segment enhanced">
                    <animate attributeName="height" :values="`0;${getBarHeight('call_scheduled')}`" dur="1.5s" begin="0.3s" fill="freeze"/>
                    <animate attributeName="y" :values="`250;${250 - getBarHeight('call_scheduled')}`" dur="1.5s" begin="0.3s" fill="freeze"/>
                  </rect>
                  <ellipse :cx="190" :cy="250 - getBarHeight('call_scheduled')" rx="30" ry="4" fill="#a78bfa" opacity="0.8" class="bar-top">
                    <animate attributeName="opacity" values="0;0.8" dur="0.5s" begin="1.8s" fill="freeze"/>
                  </ellipse>
                  
                  <!-- Bar 3 - Quote Received -->
                  <rect x="240" :y="250 - getBarHeight('quote_received')" width="60" :height="getBarHeight('quote_received')" 
                        fill="url(#barGradient3Enhanced)" rx="8" 
                        filter="url(#barShadow)"
                        class="bar-segment enhanced">
                    <animate attributeName="height" :values="`0;${getBarHeight('quote_received')}`" dur="1.5s" begin="0.6s" fill="freeze"/>
                    <animate attributeName="y" :values="`250;${250 - getBarHeight('quote_received')}`" dur="1.5s" begin="0.6s" fill="freeze"/>
                  </rect>
                  <ellipse :cx="270" :cy="250 - getBarHeight('quote_received')" rx="30" ry="4" fill="#fbbf24" opacity="0.8" class="bar-top">
                    <animate attributeName="opacity" values="0;0.8" dur="0.5s" begin="2.1s" fill="freeze"/>
                  </ellipse>
                  
                  <!-- Bar 4 - Deal Closed -->
                  <rect x="320" :y="250 - getBarHeight('deal_closed')" width="60" :height="getBarHeight('deal_closed')" 
                        fill="url(#barGradient4Enhanced)" rx="8" 
                        filter="url(#barShadow)"
                        class="bar-segment enhanced">
                    <animate attributeName="height" :values="`0;${getBarHeight('deal_closed')}`" dur="1.5s" begin="0.9s" fill="freeze"/>
                    <animate attributeName="y" :values="`250;${250 - getBarHeight('deal_closed')}`" dur="1.5s" begin="0.9s" fill="freeze"/>
                  </rect>
                  <ellipse :cx="350" :cy="250 - getBarHeight('deal_closed')" rx="30" ry="4" fill="#4ade80" opacity="0.8" class="bar-top">
                    <animate attributeName="opacity" values="0;0.8" dur="0.5s" begin="2.4s" fill="freeze"/>
                  </ellipse>
                  
                  <!-- Bar 5 - Follow-up -->
                  <rect x="400" :y="250 - getBarHeight('followup')" width="60" :height="getBarHeight('followup')" 
                        fill="url(#barGradient5Enhanced)" rx="8" 
                        filter="url(#barShadow)"
                        class="bar-segment enhanced">
                    <animate attributeName="height" :values="`0;${getBarHeight('followup')}`" dur="1.5s" begin="1.2s" fill="freeze"/>
                    <animate attributeName="y" :values="`250;${250 - getBarHeight('followup')}`" dur="1.5s" begin="1.2s" fill="freeze"/>
                  </rect>
                  <ellipse :cx="430" :cy="250 - getBarHeight('followup')" rx="30" ry="4" fill="#fb7185" opacity="0.8" class="bar-top">
                    <animate attributeName="opacity" values="0;0.8" dur="0.5s" begin="2.7s" fill="freeze"/>
                  </ellipse>
                </g>

                <!-- Enhanced value labels -->
                <g class="value-labels">
                  <text x="110" :y="240 - getBarHeight('new_lead')" text-anchor="middle" class="bar-value enhanced">{{ getStatusCount('new_lead') }}</text>
                  <text x="190" :y="240 - getBarHeight('call_scheduled')" text-anchor="middle" class="bar-value enhanced">{{ getStatusCount('call_scheduled') }}</text>
                  <text x="270" :y="240 - getBarHeight('quote_received')" text-anchor="middle" class="bar-value enhanced">{{ getStatusCount('quote_received') }}</text>
                  <text x="350" :y="240 - getBarHeight('deal_closed')" text-anchor="middle" class="bar-value enhanced">{{ getStatusCount('deal_closed') }}</text>
                  <text x="430" :y="240 - getBarHeight('followup')" text-anchor="middle" class="bar-value enhanced">{{ getStatusCount('followup') }}</text>
                </g>

                <!-- X-axis labels -->
                <g class="x-axis-labels">
                  <text x="110" y="275" text-anchor="middle" class="axis-label">לידים חדשים</text>
                  <text x="190" y="275" text-anchor="middle" class="axis-label">שיחות</text>
                  <text x="270" y="275" text-anchor="middle" class="axis-label">הצעות</text>
                  <text x="350" y="275" text-anchor="middle" class="axis-label">עסקאות</text>
                  <text x="430" y="275" text-anchor="middle" class="axis-label">מעקב</text>
                </g>
              </svg>
            </div>
          </div>

          <!-- Legend -->
          <div class="analytics-legend">
            <div class="legend-item" 
                 :class="{ active: hoveredSegment === 'new_lead' }"
                 @mouseenter="hoveredSegment = 'new_lead'"
                 @mouseleave="hoveredSegment = null">
              <div class="legend-color new-lead"></div>
              <div class="legend-info">
                <span class="legend-label">לידים חדשים</span>
                <span class="legend-value">{{ getStatusCount('new_lead') }} ({{ getStatusPercentage('new_lead').toFixed(1) }}%)</span>
              </div>
            </div>
            
            <div class="legend-item" 
                 :class="{ active: hoveredSegment === 'call_scheduled' }"
                 @mouseenter="hoveredSegment = 'call_scheduled'"
                 @mouseleave="hoveredSegment = null">
              <div class="legend-color call-scheduled"></div>
              <div class="legend-info">
                <span class="legend-label">שיחות מתוזמנות</span>
                <span class="legend-value">{{ getStatusCount('call_scheduled') }} ({{ getStatusPercentage('call_scheduled').toFixed(1) }}%)</span>
              </div>
            </div>
            
            <div class="legend-item" 
                 :class="{ active: hoveredSegment === 'quote_received' }"
                 @mouseenter="hoveredSegment = 'quote_received'"
                 @mouseleave="hoveredSegment = null">
              <div class="legend-color quote-received"></div>
              <div class="legend-info">
                <span class="legend-label">הצעות מחיר</span>
                <span class="legend-value">{{ getStatusCount('quote_received') }} ({{ getStatusPercentage('quote_received').toFixed(1) }}%)</span>
              </div>
            </div>
            
            <div class="legend-item" 
                 :class="{ active: hoveredSegment === 'deal_closed' }"
                 @mouseenter="hoveredSegment = 'deal_closed'"
                 @mouseleave="hoveredSegment = null">
              <div class="legend-color deal-closed"></div>
              <div class="legend-info">
                <span class="legend-label">עסקאות סגורות</span>
                <span class="legend-value">{{ getStatusCount('deal_closed') }} ({{ getStatusPercentage('deal_closed').toFixed(1) }}%)</span>
              </div>
            </div>
            
            <div class="legend-item" 
                 :class="{ active: hoveredSegment === 'followup' }"
                 @mouseenter="hoveredSegment = 'followup'"
                 @mouseleave="hoveredSegment = null">
              <div class="legend-color followup"></div>
              <div class="legend-info">
                <span class="legend-label">מעקב</span>
                <span class="legend-value">{{ getStatusCount('followup') }} ({{ getStatusPercentage('followup').toFixed(1) }}%)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Leads List Tab Content -->
    <div v-if="activeTab === 'leads'" class="tab-content">
      <!-- Leads List Section -->
      <div class="leads-section">
        <div class="section-header">
          <div class="header-left">
            <h2 class="section-title">
              <i class="ri-table-line"></i>
              <span>רשימת לידים</span>
            </h2>
            <p class="section-subtitle">ניהול ומעקב אחר כל הלידים</p>
          </div>
          <div class="header-right">
            <div class="search-container">
              <i class="ri-search-line search-icon"></i>
              <input
                v-model="searchQuery"
                type="text"
                placeholder="חיפוש לידים..."
                class="search-input"
              />
            </div>
            <div class="filter-controls">
              <button class="filter-btn" @click="showFilters = !showFilters">
                <i class="ri-filter-line"></i>
                <span>פילטרים</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Filters Panel -->
        <div v-if="showFilters" class="filters-panel">
          <div class="filter-group">
            <label class="filter-label">סטטוס:</label>
            <select v-model="statusFilter" class="filter-select">
              <option value="">כל הסטטוסים</option>
              <option value="new_lead">ליד חדש</option>
              <option value="call_scheduled">שיחה מתוזמנת</option>
              <option value="quote_received">הצעת מחיר</option>
              <option value="deal_closed">עסקה סגורה</option>
              <option value="followup">מעקב</option>
              <option value="not_relevant">לא רלוונטי</option>
            </select>
          </div>
          <div class="filter-group">
            <label class="filter-label">מקור:</label>
            <select v-model="sourceFilter" class="filter-select">
              <option value="">כל המקורות</option>
              <option value="website">אתר</option>
              <option value="facebook">פייסבוק</option>
              <option value="google">גוגל</option>
              <option value="referral">הפניה</option>
              <option value="other">אחר</option>
            </select>
          </div>
          <div class="filter-group">
            <label class="filter-label">תאריך:</label>
            <select v-model="dateFilter" class="filter-select">
              <option value="">כל התאריכים</option>
              <option value="today">היום</option>
              <option value="week">השבוע</option>
              <option value="month">החודש</option>
              <option value="quarter">הרבעון</option>
            </select>
          </div>
          <button class="clear-filters-btn" @click="clearFilters">
            <i class="ri-close-line"></i>
            <span>נקה פילטרים</span>
          </button>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="loading-section">
          <div class="loading-content">
            <div class="spinner"></div>
            <p class="loading-text">טוען לידים...</p>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else-if="filteredLeads.length === 0" class="empty-section">
          <div class="empty-content">
            <div class="empty-icon">
              <i class="ri-user-star-line"></i>
            </div>
            <h3 class="empty-title">לא נמצאו לידים</h3>
            <p class="empty-description">
              {{ searchQuery ? 'לא נמצאו לידים התואמים לחיפוש שלך.' : 'התחל בהוספת הליד הראשון שלך למערכת.' }}
            </p>
            <button class="action-btn primary" @click="showCreateModal = true">
              <i class="ri-add-line"></i>
              <span>הוסף ליד ראשון</span>
            </button>
          </div>
        </div>

        <!-- Leads Table -->
        <div v-else class="leads-table-container">
          <div class="table-header-actions">
            <div class="bulk-actions" v-if="selectedLeads.length > 0">
              <span class="selected-count">{{ selectedLeads.length }} נבחרו</span>
              <button class="bulk-btn delete" @click="bulkDelete">
                <i class="ri-delete-bin-line"></i>
                <span>מחק</span>
              </button>
              <button class="bulk-btn export" @click="bulkExport">
                <i class="ri-download-line"></i>
                <span>ייצא</span>
              </button>
            </div>
            <div class="table-info">
              <span class="results-count">{{ filteredLeads.length }} לידים נמצאו</span>
            </div>
          </div>

          <div class="table-wrapper">
            <table class="leads-table">
              <thead>
                <tr>
                  <th class="table-header checkbox-col">
                    <input 
                      type="checkbox" 
                      v-model="selectAll" 
                      @change="toggleSelectAll"
                      class="checkbox"
                    />
                  </th>
                  <th class="table-header">ליד</th>
                  <th class="table-header">פרטי קשר</th>
                  <th class="table-header">חברה</th>
                  <th class="table-header">סטטוס</th>
                  <th class="table-header">מקור</th>
                  <th class="table-header">תאריך יצירה</th>
                  <th class="table-header actions-col">פעולות</th>
                </tr>
              </thead>
              <tbody>
                <tr 
                  v-for="lead in paginatedLeads" 
                  :key="lead.id"
                  class="table-row"
                  @click="viewLeadDetails(lead)"
                >
                  <td class="table-cell checkbox-col">
                    <input 
                      type="checkbox" 
                      :value="lead.id"
                      v-model="selectedLeads"
                      @click.stop
                      class="checkbox"
                    />
                  </td>
                  <td class="table-cell">
                    <div class="lead-info">
                      <div class="lead-avatar">
                        <img
                          :src="getLeadAvatar(lead)"
                          :alt="lead.name"
                          class="avatar-image"
                        />
                        <div class="status-indicator" :class="getStatusClass(lead.status)"></div>
                      </div>
                      <div class="lead-details">
                        <h3 class="lead-name">{{ lead.name }}</h3>
                        <p class="lead-id">ID: {{ getShortId(lead.id) }}</p>
                      </div>
                    </div>
                  </td>
                  <td class="table-cell">
                    <div class="contact-info">
                      <div class="contact-item">
                        <i class="ri-mail-line"></i>
                        <span>{{ lead.email }}</span>
                      </div>
                      <div class="contact-item" v-if="lead.phone">
                        <i class="ri-phone-line"></i>
                        <span>{{ lead.phone }}</span>
                      </div>
                    </div>
                  </td>
                  <td class="table-cell">
                    <span class="company-name">{{ lead.company || 'ללא חברה' }}</span>
                  </td>
                  <td class="table-cell">
                    <span class="status-badge" :class="getStatusClass(lead.status)">
                      {{ getStatusText(lead.status) }}
                    </span>
                  </td>
                  <td class="table-cell">
                    <span class="source-badge">{{ lead.source || 'לא ידוע' }}</span>
                  </td>
                  <td class="table-cell">
                    <div class="date-info">
                      <span class="date-text">{{ formatDate(lead.created_at) }}</span>
                    </div>
                  </td>
                  <td class="table-cell actions-col">
                    <div class="table-actions">
                      <button 
                        class="action-icon edit"
                        @click.stop="editLead(lead)"
                        title="ערוך ליד"
                      >
                        <i class="ri-edit-line"></i>
                      </button>
                      <button 
                        class="action-icon delete"
                        @click.stop="deleteLead(lead.id)"
                        title="מחק ליד"
                      >
                        <i class="ri-delete-bin-line"></i>
                      </button>
                      <button 
                        class="action-icon more"
                        @click.stop="showLeadMenu(lead)"
                        title="עוד פעולות"
                      >
                        <i class="ri-more-2-line"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div class="pagination-section" v-if="filteredLeads.length > itemsPerPage">
            <div class="pagination-info">
              מציג {{ startIndex + 1 }} עד {{ endIndex }} מתוך {{ filteredLeads.length }} לידים
            </div>
            <div class="pagination-controls">
              <button 
                class="pagination-btn" 
                :disabled="currentPage === 1"
                @click="currentPage--"
              >
                <i class="ri-arrow-right-line"></i>
                <span>הקודם</span>
              </button>
              <div class="page-numbers">
                <button 
                  v-for="page in visiblePages" 
                  :key="page"
                  class="page-btn"
                  :class="{ active: page === currentPage }"
                  @click="currentPage = page"
                >
                  {{ page }}
                </button>
              </div>
              <button 
                class="pagination-btn" 
                :disabled="currentPage === totalPages"
                @click="currentPage++"
              >
                <span>הבא</span>
                <i class="ri-arrow-left-line"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create Lead Modal -->
    <div v-if="showCreateModal" class="modal-overlay" @click="closeModal">
      <div class="modal-container" @click.stop>
        <div class="modal-header">
          <div class="modal-title-section">
            <h3 class="modal-title">
              <i class="ri-user-add-line"></i>
              <span>הוסף ליד חדש</span>
            </h3>
            <p class="modal-subtitle">צור ליד חדש בצינור המכירות</p>
          </div>
          <button class="modal-close" @click="closeModal">
            <i class="ri-close-line"></i>
          </button>
        </div>
        
        <form @submit.prevent="createLead" class="modal-form">
          <div class="form-grid">
            <div class="form-group">
              <label class="form-label">
                <i class="ri-user-line"></i>
                <span>שם מלא *</span>
              </label>
              <input
                v-model="newLead.name"
                type="text"
                class="form-input"
                placeholder="הכנס שם מלא"
                required
              />
            </div>

            <div class="form-group">
              <label class="form-label">
                <i class="ri-mail-line"></i>
                <span>אימייל *</span>
              </label>
              <input
                v-model="newLead.email"
                type="email"
                class="form-input"
                placeholder="הכנס כתובת אימייל"
                required
              />
            </div>

            <div class="form-group">
              <label class="form-label">
                <i class="ri-phone-line"></i>
                <span>טלפון</span>
              </label>
              <input
                v-model="newLead.phone"
                type="tel"
                class="form-input"
                placeholder="הכנס מספר טלפון"
              />
            </div>

            <div class="form-group">
              <label class="form-label">
                <i class="ri-building-line"></i>
                <span>חברה</span>
              </label>
              <input
                v-model="newLead.company"
                type="text"
                class="form-input"
                placeholder="הכנס שם חברה"
              />
            </div>

            <div class="form-group">
              <label class="form-label">
                <i class="ri-flag-line"></i>
                <span>סטטוס</span>
              </label>
              <select v-model="newLead.status" class="form-select">
                <option value="new_lead">ליד חדש</option>
                <option value="call_scheduled">שיחה מתוזמנת</option>
                <option value="quote_received">הצעת מחיר</option>
                <option value="deal_closed">עסקה סגורה</option>
                <option value="followup">מעקב</option>
                <option value="not_relevant">לא רלוונטי</option>
              </select>
            </div>

            <div class="form-group">
              <label class="form-label">
                <i class="ri-links-line"></i>
                <span>מקור</span>
              </label>
              <select v-model="newLead.source" class="form-select">
                <option value="">בחר מקור</option>
                <option value="website">אתר</option>
                <option value="facebook">פייסבוק</option>
                <option value="google">גוגל</option>
                <option value="referral">הפניה</option>
                <option value="other">אחר</option>
              </select>
            </div>
          </div>

          <div class="form-group full-width">
            <label class="form-label">
              <i class="ri-file-text-line"></i>
              <span>הערות</span>
            </label>
            <textarea
              v-model="newLead.notes"
              class="form-textarea"
              placeholder="הכנס הערות נוספות..."
              rows="3"
            ></textarea>
          </div>

          <div class="modal-actions">
            <button type="button" class="action-btn secondary" @click="closeModal">
              <span>ביטול</span>
            </button>
            <button type="submit" class="action-btn primary" :disabled="isCreating">
              <i v-if="isCreating" class="ri-loader-4-line animate-spin"></i>
              <i v-else class="ri-add-line"></i>
              <span>{{ isCreating ? 'יוצר...' : 'צור ליד' }}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from "vue";
import { supabase } from "@/lib/supabaseClient";

interface Lead {
  id: string;
  name: string;
  email: string;
  phone?: string;
  status: string;
  source?: string;
  company?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
  org_id: string;
}

export default defineComponent({
  name: "NewLeads",
  setup() {
    // State
    const loading = ref(true);
    const leads = ref<Lead[]>([]);
    const searchQuery = ref('');
    const statusFilter = ref('all');
    const dateFilter = ref('all');
    const timeFilter = ref('360');
    const currentPage = ref(1);
    const itemsPerPage = ref(10);
    const selectedLeads = ref<string[]>([]);
    const selectAll = ref(false);
    const showCreateModal = ref(false);
    const newLead = ref({
      name: '',
      email: '',
      phone: '',
      company: '',
      status: 'new_lead',
      source: '',
      notes: '',
    });
    const isCreating = ref(false);
    const showFilters = ref(false);
    const sourceFilter = ref('');
    const activeTab = ref('dashboard');

    // Stats
    const totalLeads = ref(0);
    const hotLeads = ref(0);
    const conversions = ref(0);
    const revenue = ref(0);

    // Analytics
    const analyticsView = ref('pie');
    const hoveredSegment = ref<string | null>(null);

    // Computed
    const filteredLeads = computed(() => {
      let filtered = leads.value;

      // Search filter
      if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase();
        filtered = filtered.filter(lead => 
          lead.name.toLowerCase().includes(query) ||
          lead.email.toLowerCase().includes(query) ||
          (lead.phone && lead.phone.toLowerCase().includes(query)) ||
          (lead.company && lead.company.toLowerCase().includes(query))
        );
      }

      // Status filter
      if (statusFilter.value && statusFilter.value !== 'all') {
        filtered = filtered.filter(lead => lead.status === statusFilter.value);
      }

      // Source filter
      if (sourceFilter.value) {
        filtered = filtered.filter(lead => lead.source === sourceFilter.value);
      }

      // Date filter
      if (dateFilter.value && dateFilter.value !== 'all') {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        
        filtered = filtered.filter(lead => {
          const leadDate = new Date(lead.created_at);
          
          switch (dateFilter.value) {
            case 'today':
              return leadDate >= today;
            case 'week': {
              const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
              return leadDate >= weekAgo;
            }
            case 'month': {
              const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
              return leadDate >= monthAgo;
            }
            case 'quarter': {
              const quarterAgo = new Date(today.getTime() - 90 * 24 * 60 * 60 * 1000);
              return leadDate >= quarterAgo;
            }
            default:
              return true;
          }
        });
      }

      return filtered;
    });

    const paginatedLeads = computed(() => {
      const start = (currentPage.value - 1) * itemsPerPage.value;
      const end = start + itemsPerPage.value;
      return filteredLeads.value.slice(start, end);
    });

    const totalPages = computed(() => {
      return Math.ceil(filteredLeads.value.length / itemsPerPage.value);
    });

    const startIndex = computed(() => {
      return (currentPage.value - 1) * itemsPerPage.value;
    });

    const endIndex = computed(() => {
      return Math.min(startIndex.value + itemsPerPage.value, filteredLeads.value.length);
    });

    const conversionRate = computed(() => {
      if (leads.value.length === 0) return 0;
      return ((conversions.value / leads.value.length) * 100).toFixed(1);
    });

    const visiblePages = computed(() => {
      const pages = [];
      const maxVisible = 5;
      const start = Math.max(1, currentPage.value - Math.floor(maxVisible / 2));
      const end = Math.min(totalPages.value, start + maxVisible - 1);
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    });

    // Methods
    const fetchLeads = async () => {
      try {
        loading.value = true;
        
        // Get current user
        const { data: userData, error: userError } = await supabase.auth.getUser();
        if (userError) throw userError;

        if (!userData.user) return;

        // Get user's organization
        const { data: profileData, error: profileError } = await supabase
          .from('user_profiles')
          .select('org_id')
          .eq('user_id', userData.user.id)
          .single();

        if (profileError) throw profileError;
        if (!profileData?.org_id) return;

        // Fetch leads
        const { data, error } = await supabase
          .from('leads')
          .select('*')
          .eq('org_id', profileData.org_id)
          .order('created_at', { ascending: false });

        if (error) throw error;

                 leads.value = (data || []) as unknown as Lead[];
        calculateStats();
      } catch (error) {
        console.error('Error fetching leads:', error);
      } finally {
        loading.value = false;
      }
    };

    const calculateStats = () => {
      totalLeads.value = leads.value.length;
      hotLeads.value = leads.value.filter(lead => lead.status === 'new_lead').length;
      conversions.value = leads.value.filter(lead => lead.status === 'deal_closed').length;
      revenue.value = conversions.value * 5000; // Mock calculation
    };

    const getLeadAvatar = (lead: Lead) => {
      // Return a default avatar or generate one based on name
      return `https://ui-avatars.com/api/?name=${encodeURIComponent(lead.name)}&background=random`;
    };

    const getStatusClass = (status: string) => {
      const statusClasses = {
        'new_lead': 'status-new',
        'call_scheduled': 'status-scheduled',
        'quote_received': 'status-quote',
        'deal_closed': 'status-closed',
        'not_relevant': 'status-irrelevant',
        'followup': 'status-followup'
      };
      return statusClasses[status as keyof typeof statusClasses] || 'status-default';
    };

    const getStatusText = (status: string) => {
      const statusTexts = {
        'new_lead': 'ליד חדש',
        'call_scheduled': 'נקבעה שיחה',
        'quote_received': 'קיבל הצעת מחיר',
        'deal_closed': 'סגר עסקה',
        'not_relevant': 'לא רלוונטי',
        'followup': 'פולואפ'
      };
      return statusTexts[status as keyof typeof statusTexts] || status;
    };

    const getShortId = (id: string) => {
      return id.substring(0, 8);
    };

    const formatDate = (dateString: string) => {
      const date = new Date(dateString);
      return date.toLocaleDateString('he-IL');
    };

    const toggleSelectAll = () => {
      if (selectAll.value) {
        selectedLeads.value = paginatedLeads.value.map(lead => lead.id);
      } else {
        selectedLeads.value = [];
      }
    };

    const addNewLead = () => {
      // TODO: Open add lead modal
      console.log('Add new lead');
    };

    const exportLeads = () => {
      const csvContent = [
        ['שם', 'אימייל', 'טלפון', 'חברה', 'סטטוס', 'מקור', 'תאריך יצירה'].join(','),
        ...filteredLeads.value.map(lead => [
          lead.name,
          lead.email,
          lead.phone || '',
          lead.company || '',
          getStatusText(lead.status),
          lead.source || '',
          formatDate(lead.created_at)
        ].join(','))
      ].join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `all_leads_export_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    const viewLead = (lead: Lead) => {
      // TODO: Open lead details
      console.log('View lead:', lead);
    };

    const editLead = (lead: Lead) => {
      // TODO: Open edit lead modal
      console.log('Edit lead:', lead);
    };

    const deleteLead = async (leadId: string) => {
      try {
        if (!confirm('האם אתה בטוח שברצונך למחוק את הליד הזה?')) return;
        
        const { error } = await supabase
          .from('leads')
          .delete()
          .eq('id', leadId);
          
        if (error) throw error;
        
        // Remove from local state
        leads.value = leads.value.filter(lead => lead.id !== leadId);
        calculateStats();
      } catch (error) {
        console.error('Error deleting lead:', error);
      }
    };

    const getCustomerHandle = (name: string) => {
      return name.toLowerCase().replace(/\s+/g, '');
    };

    const getLifetimeValue = () => {
      return (Math.random() * 5000 + 1000).toFixed(2);
    };

    const getLifetimeChange = () => {
      const change = parseFloat((Math.random() * 50 - 25).toFixed(1));
      return `${change > 0 ? '↑' : '↓'} ${Math.abs(change)}%`;
    };

    const getChangeClass = () => {
      return Math.random() > 0.5 ? 'positive' : 'negative';
    };

    const getPurchasedCount = () => {
      return Math.floor(Math.random() * 100) + 1;
    };

    const getCommentsCount = () => {
      return Math.floor(Math.random() * 1000) + 50;
    };

    const getLikesCount = () => {
      return Math.floor(Math.random() * 1000) + 100;
    };

    const getStatusCount = (status: string) => {
      return leads.value.filter(lead => lead.status === status).length;
    };

    const getStatusPercentage = (status: string) => {
      if (totalLeads.value === 0) return 0;
      return (getStatusCount(status) / totalLeads.value) * 100;
    };

    const getPieSegmentPath = (startPercentage: number, segmentPercentage: number) => {
      const radius = 100; // Updated for the new larger pie chart
      const centerX = 140;
      const centerY = 140;
      
      const startAngle = (startPercentage / 100) * 2 * Math.PI - Math.PI / 2;
      const endAngle = ((startPercentage + segmentPercentage) / 100) * 2 * Math.PI - Math.PI / 2;
      
      const x1 = centerX + radius * Math.cos(startAngle);
      const y1 = centerY + radius * Math.sin(startAngle);
      const x2 = centerX + radius * Math.cos(endAngle);
      const y2 = centerY + radius * Math.sin(endAngle);
      
      const largeArcFlag = segmentPercentage > 50 ? 1 : 0;
      
      if (segmentPercentage === 0) return '';
      
      return [
        `M ${centerX} ${centerY}`,
        `L ${x1} ${y1}`,
        `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
        'Z'
      ].join(' ');
    };

    const getBarHeight = (status: string) => {
      const maxHeight = 180; // Updated for the new chart size
      const count = getStatusCount(status);
      const maxCount = Math.max(...['new_lead', 'call_scheduled', 'quote_received', 'deal_closed', 'followup'].map(s => getStatusCount(s)));
      
      if (maxCount === 0) return 0;
      
      // Ensure minimum height for visibility and smooth animation
      const minHeight = 8;
      const calculatedHeight = (count / maxCount) * maxHeight;
      
      return Math.max(calculatedHeight, count > 0 ? minHeight : 0);
    };

    const createLead = async () => {
      try {
        isCreating.value = true;
        
        // Get current user
        const { data: userData, error: userError } = await supabase.auth.getUser();
        if (userError) throw userError;

        if (!userData.user) return;

        // Get user's organization
        const { data: profileData, error: profileError } = await supabase
          .from('user_profiles')
          .select('org_id')
          .eq('user_id', userData.user.id)
          .single();

        if (profileError) throw profileError;
        if (!profileData?.org_id) return;

        // Create new lead
        const { data, error } = await supabase
          .from('leads')
          .insert([
            {
              name: newLead.value.name,
              email: newLead.value.email,
              phone: newLead.value.phone,
              company: newLead.value.company,
              status: newLead.value.status,
              source: newLead.value.source,
              notes: newLead.value.notes,
              org_id: profileData.org_id,
            },
          ])
          .select('*');

        if (error) throw error;

        // Add new lead to existing leads
        if (data && data.length > 0) {
          leads.value.unshift(data[0] as unknown as Lead);
          calculateStats();
        }
        
        // Reset form and close modal
        newLead.value = {
          name: '',
          email: '',
          phone: '',
          company: '',
          status: 'new_lead',
          source: '',
          notes: '',
        };
        showCreateModal.value = false;
      } catch (error) {
        console.error('Error creating lead:', error);
      } finally {
        isCreating.value = false;
      }
    };

    const closeModal = () => {
      showCreateModal.value = false;
    };

    const applyTimeFilter = () => {
      // Filter implementation based on timeFilter.value
      fetchLeads();
    };

    const viewLeadDetails = (lead: Lead) => {
      // Navigate to lead details page
      console.log('View lead details:', lead);
    };

    const toggleStatusFilter = (status: string) => {
      if (statusFilter.value === status) {
        statusFilter.value = '';
      } else {
        statusFilter.value = status;
      }
    };

    const clearFilters = () => {
      searchQuery.value = '';
      statusFilter.value = '';
      sourceFilter.value = '';
      dateFilter.value = '';
    };

    const bulkDelete = async () => {
      if (selectedLeads.value.length === 0) return;
      
      if (!confirm(`האם אתה בטוח שברצונך למחוק ${selectedLeads.value.length} לידים?`)) return;
      
      try {
        const { error } = await supabase
          .from('leads')
          .delete()
          .in('id', selectedLeads.value);
          
        if (error) throw error;
        
        // Remove from local state
        leads.value = leads.value.filter(lead => !selectedLeads.value.includes(lead.id));
        selectedLeads.value = [];
        selectAll.value = false;
        calculateStats();
      } catch (error) {
        console.error('Error deleting leads:', error);
      }
    };

    const bulkExport = () => {
      if (selectedLeads.value.length === 0) return;
      
      const selectedLeadsData = leads.value.filter(lead => selectedLeads.value.includes(lead.id));
      const csvContent = [
        ['שם', 'אימייל', 'טלפון', 'חברה', 'סטטוס', 'מקור', 'תאריך יצירה'].join(','),
        ...selectedLeadsData.map(lead => [
          lead.name,
          lead.email,
          lead.phone || '',
          lead.company || '',
          getStatusText(lead.status),
          lead.source || '',
          formatDate(lead.created_at)
        ].join(','))
      ].join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `leads_export_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    const showLeadMenu = (lead: Lead) => {
      // This could open a context menu or dropdown with more actions
      console.log('Show lead menu for:', lead);
    };

    // Lifecycle
    onMounted(() => {
      fetchLeads();
    });

    return {
      // State
      loading,
      leads,
      searchQuery,
      statusFilter,
      dateFilter,
      timeFilter,
      currentPage,
      itemsPerPage,
      selectedLeads,
      selectAll,
      showCreateModal,
      newLead,
      isCreating,
      showFilters,
      sourceFilter,
      activeTab,
      
      // Stats
      totalLeads,
      hotLeads,
      conversions,
      revenue,
      
      // Analytics
      analyticsView,
      hoveredSegment,
      
      // Computed
      filteredLeads,
      paginatedLeads,
      totalPages,
      startIndex,
      endIndex,
      conversionRate,
      visiblePages,
      
      // Methods
      getLeadAvatar,
      getStatusClass,
      getStatusText,
      getShortId,
      formatDate,
      toggleSelectAll,
      addNewLead,
      exportLeads,
      viewLead,
      editLead,
      deleteLead,
      getCustomerHandle,
      getLifetimeValue,
      getLifetimeChange,
      getChangeClass,
      getPurchasedCount,
      getCommentsCount,
      getLikesCount,
      getStatusCount,
      getStatusPercentage,
      getPieSegmentPath,
      getBarHeight,
      createLead,
      closeModal,
      applyTimeFilter,
      viewLeadDetails,
      toggleStatusFilter,
      clearFilters,
      bulkDelete,
      bulkExport,
      showLeadMenu,
    };
  },
});
</script>

<style scoped>
/* Design System Variables */
:root {
  --color-primary: #111827;
  --color-primary-light: #374151;
  --color-secondary: #6b7280;
  --color-accent: #8b5cf6;
  --color-success: #10b981;
  --color-warning: #f59e0b;
  --color-error: #ef4444;
  --color-info: #3b82f6;
  
  --color-bg-primary: #ffffff;
  --color-bg-secondary: #f8fafc;
  --color-bg-tertiary: #f1f5f9;
  --color-bg-quaternary: #e2e8f0;
  
  --color-text-primary: #111827;
  --color-text-secondary: #374151;
  --color-text-tertiary: #6b7280;
  --color-text-quaternary: #9ca3af;
  
  --color-border-primary: #e5e7eb;
  --color-border-secondary: #d1d5db;
  --color-border-tertiary: #f3f4f6;
  
  --spacing-xs: 0.25rem;
  --spacing-sm: 0.5rem;
  --spacing-md: 0.75rem;
  --spacing-lg: 1rem;
  --spacing-xl: 1.5rem;
  --spacing-2xl: 2rem;
  --spacing-3xl: 3rem;
  
  --radius-sm: 0.5rem;
  --radius-md: 0.75rem;
  --radius-lg: 1rem;
  
  --shadow-sm: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  --shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  --shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  --shadow-xl: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  
  --transition-fast: 0.15s ease;
  --transition-normal: 0.2s ease;
  --transition-slow: 0.3s ease;
}

/* Main Container */
.leads-dashboard {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  padding: var(--spacing-xl);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  line-height: 1.5;
  color: var(--color-text-primary);
  direction: rtl;
}

/* Page Header */
.dashboard-header {
  margin-bottom: var(--spacing-2xl);
  max-width: 1400px;
  margin-left: auto;
  margin-right: auto;
}

.header-content {
  background: var(--color-bg-primary);
  border-radius: var(--radius-lg);
  padding: var(--spacing-2xl);
  box-shadow: var(--shadow-lg);
  border: 1px solid var(--color-border-tertiary);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--spacing-2xl);
}

.header-text {
  flex: 1;
}

.page-title {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  font-size: 1.875rem;
  font-weight: 700;
  color: var(--color-text-primary);
  margin: 0 0 var(--spacing-sm) 0;
  line-height: 1.2;
}

.title-icon {
  color: var(--color-accent);
  font-size: 2rem;
}

.page-subtitle {
  color: var(--color-text-tertiary);
  font-size: 1rem;
  font-weight: 400;
  margin: 0;
  line-height: 1.4;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: var(--spacing-lg);
}

/* Action Buttons */
.action-btn {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-md) var(--spacing-xl);
  border: none;
  border-radius: var(--radius-md);
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all var(--transition-normal);
  text-decoration: none;
  white-space: nowrap;
}

.action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none !important;
}

.action-btn.primary {
  background: linear-gradient(135deg, var(--color-accent), #7c3aed);
  color: white;
  box-shadow: var(--shadow-md);
}

.action-btn.primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #7c3aed, var(--color-accent));
  transform: translateY(-2px);
  box-shadow: var(--shadow-lg);
}

.action-btn.secondary {
  background: var(--color-bg-primary);
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border-secondary);
  box-shadow: var(--shadow-sm);
}

.action-btn.secondary:hover:not(:disabled) {
  background: var(--color-bg-tertiary);
  transform: translateY(-1px);
  box-shadow: var(--shadow-md);
}

/* Dashboard Grid */
.dashboard-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: var(--spacing-xl);
  margin-bottom: var(--spacing-2xl);
  max-width: 1400px;
  margin-left: auto;
  margin-right: auto;
}

.dashboard-card {
  background: var(--color-bg-primary);
  border-radius: var(--radius-lg);
  padding: var(--spacing-xl);
  box-shadow: var(--shadow-lg);
  border: 1px solid var(--color-border-tertiary);
  transition: all var(--transition-normal);
  position: relative;
  overflow: hidden;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.dashboard-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-xl);
}

.dashboard-card.total-leads .card-icon {
  background: linear-gradient(135deg, var(--color-info), #2563eb);
}

.dashboard-card.hot-leads .card-icon {
  background: linear-gradient(135deg, var(--color-error), #dc2626);
}

.dashboard-card.conversions .card-icon {
  background: linear-gradient(135deg, var(--color-success), #059669);
}

.dashboard-card.revenue .card-icon {
  background: linear-gradient(135deg, var(--color-accent), #7c3aed);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--spacing-md);
}

.card-icon {
  width: 3.5rem;
  height: 3.5rem;
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: white;
  flex-shrink: 0;
  box-shadow: var(--shadow-md);
}

.card-menu {
  color: var(--color-text-quaternary);
  cursor: pointer;
  padding: var(--spacing-xs);
  border-radius: var(--radius-sm);
  transition: all var(--transition-fast);
}

.card-menu:hover {
  color: var(--color-text-secondary);
  background: var(--color-bg-tertiary);
}

.card-content {
  flex: 1;
}

.card-title {
  font-size: 0.875rem;
  color: var(--color-text-secondary);
  margin: 0 0 var(--spacing-sm) 0;
  font-weight: 600;
  line-height: 1.2;
}

.card-value {
  font-size: 2rem;
  font-weight: 800;
  color: var(--color-text-primary);
  margin: var(--spacing-sm) 0;
  line-height: 1.1;
}

.card-change {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  font-size: 0.75rem;
  font-weight: 600;
  margin-bottom: var(--spacing-md);
}

.card-change.positive {
  color: var(--color-success);
}

.card-change.negative {
  color: var(--color-error);
}

.change-period {
  color: var(--color-text-quaternary);
  font-weight: 400;
}

.card-chart {
  width: 100%;
  height: 40px;
  opacity: 0.8;
  margin-top: auto;
}

.mini-chart {
  width: 100%;
  height: 100%;
  overflow: visible;
  cursor: pointer;
  transition: all var(--transition-normal);
}

.dashboard-card:hover .mini-chart {
  transform: scale(1.03);
  filter: brightness(1.05);
}

/* Chart Line Animations */
.chart-line {
  stroke-dasharray: 200;
  stroke-dashoffset: 200;
  transition: all var(--transition-normal);
}

.dashboard-card:hover .chart-line {
  filter: brightness(1.2);
  stroke-width: 4;
}

/* Chart Fill Effects */
.chart-fill {
  transition: all var(--transition-normal);
}

.dashboard-card:hover .chart-fill {
  opacity: 0.9 !important;
}

/* Data Points */
.data-point {
  transition: all var(--transition-normal);
  cursor: pointer;
}

.data-point:hover {
  r: 4;
  filter: brightness(1.3);
}

/* Flame Effects for Hot Leads */
.flame-line {
  transition: all var(--transition-normal);
}

.dashboard-card.hot-leads:hover .flame-line {
  stroke-width: 3;
  filter: brightness(1.3);
}

.flame-particle {
  transition: all var(--transition-normal);
}

.dashboard-card.hot-leads:hover .flame-particle {
  r: 2;
}

/* Success Effects for Conversions */
.success-step {
  transition: all var(--transition-normal);
  transform-origin: bottom;
}

.dashboard-card.conversions:hover .success-step {
  transform: scaleY(1.1);
  filter: brightness(1.2);
}

.success-point {
  transition: all var(--transition-normal);
}

.success-point:hover {
  r: 3;
  filter: drop-shadow(0 0 8px rgba(34, 197, 94, 0.6));
}

/* Money Flow Effects for Revenue */
.money-flow {
  transition: all var(--transition-normal);
}

.dashboard-card.revenue:hover .money-flow {
  filter: brightness(1.3) drop-shadow(0 0 10px rgba(168, 85, 247, 0.5));
}

.secondary-flow {
  transition: all var(--transition-normal);
}

.dashboard-card.revenue:hover .secondary-flow {
  opacity: 1 !important;
  stroke-width: 3;
}

.money-symbol {
  transition: all var(--transition-normal);
  transform-origin: center;
}

.dashboard-card.revenue:hover .money-symbol {
  font-size: 10px;
  filter: brightness(1.4);
  transform: scale(1.1);
}

.revenue-peak {
  transition: all var(--transition-normal);
}

.revenue-peak:hover {
  r: 3;
  filter: drop-shadow(0 0 8px rgba(168, 85, 247, 0.7));
}

/* Enhanced Card Effects */
.dashboard-card {
  position: relative;
  overflow: hidden;
  transition: all var(--transition-normal);
}

.dashboard-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, 
    transparent, 
    rgba(255, 255, 255, 0.1), 
    transparent
  );
  transition: left 0.5s ease;
  pointer-events: none;
}

.dashboard-card:hover::before {
  left: 100%;
}

.dashboard-card:hover {
  transform: translateY(-4px);
  box-shadow: 
    0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04),
    0 0 0 1px rgba(255, 255, 255, 0.1);
}

/* Card Content Enhancements */
.card-content {
  position: relative;
  z-index: 2;
}

.card-value {
  font-size: 2.5rem;
  font-weight: 800;
  line-height: 1;
  margin: var(--spacing-sm) 0;
  background: linear-gradient(135deg, var(--color-text-primary), var(--color-text-secondary));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  transition: all var(--transition-normal);
}

.dashboard-card:hover .card-value {
  transform: scale(1.05);
  filter: brightness(1.2);
}

/* Specific Card Themes */
.dashboard-card.total-leads:hover {
  background: radial-gradient(circle at top right, rgba(59, 130, 246, 0.1), transparent);
}

.dashboard-card.hot-leads:hover {
  background: radial-gradient(circle at top right, rgba(239, 68, 68, 0.1), transparent);
}

.dashboard-card.conversions:hover {
  background: radial-gradient(circle at top right, rgba(34, 197, 94, 0.1), transparent);
}

.dashboard-card.revenue:hover {
  background: radial-gradient(circle at top right, rgba(168, 85, 247, 0.1), transparent);
}

/* Chart Container Enhancements */
.card-chart {
  position: relative;
  height: 70px;
  margin-top: var(--spacing-lg);
  border-radius: var(--radius-md);
  overflow: hidden;
  background: linear-gradient(135deg, 
    rgba(255, 255, 255, 0.08), 
    rgba(255, 255, 255, 0.04)
  );
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  transition: all var(--transition-normal);
  box-shadow: 
    inset 0 1px 0 rgba(255, 255, 255, 0.1),
    0 2px 4px rgba(0, 0, 0, 0.05);
}

.dashboard-card:hover .card-chart {
  background: linear-gradient(135deg, 
    rgba(255, 255, 255, 0.12), 
    rgba(255, 255, 255, 0.06)
  );
  border-color: rgba(255, 255, 255, 0.18);
  box-shadow: 
    inset 0 1px 0 rgba(255, 255, 255, 0.15),
    0 4px 8px rgba(0, 0, 0, 0.08);
}

/* Card Icon Enhancements */
.card-icon {
  width: 3rem;
  height: 3rem;
  border-radius: var(--radius-lg);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all var(--transition-normal);
  position: relative;
  overflow: hidden;
}

.card-icon::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.3), transparent);
  transition: all 0.4s ease;
  transform: translate(-50%, -50%);
}

.dashboard-card:hover .card-icon::before {
  width: 100%;
  height: 100%;
}

.card-icon i {
  font-size: 1.5rem;
  transition: all var(--transition-normal);
  position: relative;
  z-index: 2;
}

.dashboard-card:hover .card-icon i {
  transform: scale(1.1) rotate(5deg);
  filter: brightness(1.2);
}

/* Specific Icon Colors */
.dashboard-card.total-leads .card-icon {
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
}

.dashboard-card.hot-leads .card-icon {
  background: linear-gradient(135deg, #ef4444, #dc2626);
}

.dashboard-card.conversions .card-icon {
  background: linear-gradient(135deg, #22c55e, #16a34a);
}

.dashboard-card.revenue .card-icon {
  background: linear-gradient(135deg, #a855f7, #7c3aed);
}

/* Change Indicators Enhancement */
.card-change {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  font-size: 0.875rem;
  font-weight: 600;
  padding: var(--spacing-xs) var(--spacing-sm);
  border-radius: var(--radius-sm);
  transition: all var(--transition-normal);
  position: relative;
  overflow: hidden;
}

.card-change::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, 
    transparent, 
    rgba(255, 255, 255, 0.2), 
    transparent
  );
  transition: left 0.6s ease;
}

.dashboard-card:hover .card-change::before {
  left: 100%;
}

.card-change.positive {
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.1), rgba(34, 197, 94, 0.05));
  color: var(--color-success);
  border: 1px solid rgba(34, 197, 94, 0.2);
}

.card-change.negative {
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.1), rgba(239, 68, 68, 0.05));
  color: var(--color-error);
  border: 1px solid rgba(239, 68, 68, 0.2);
}

.card-change i {
  transition: all var(--transition-normal);
}

.dashboard-card:hover .card-change i {
  transform: scale(1.2);
}

/* Responsive Chart Adjustments */
@media (max-width: 768px) {
  .card-chart {
    height: 50px;
  }
  
  .card-value {
    font-size: 2rem;
  }
  
  .mini-chart .money-symbol {
    font-size: 6px;
  }
}

/* Accessibility Enhancements */
@media (prefers-reduced-motion: reduce) {
  .chart-line,
  .chart-fill,
  .data-point,
  .flame-line,
  .flame-particle,
  .success-step,
  .success-point,
  .money-flow,
  .secondary-flow,
  .money-symbol,
  .revenue-peak {
    animation: none !important;
  }
  
  .dashboard-card:hover {
    transform: none;
  }
}

/* Pipeline Section */
.pipeline-section {
  margin-bottom: var(--spacing-2xl);
  max-width: 1400px;
  margin-left: auto;
  margin-right: auto;
}

.section-header {
  background: var(--color-bg-primary);
  border-radius: var(--radius-lg);
  padding: var(--spacing-xl);
  box-shadow: var(--shadow-lg);
  border: 1px solid var(--color-border-tertiary);
  margin-bottom: var(--spacing-xl);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.section-title {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--color-text-primary);
  margin: 0;
  line-height: 1.2;
}

.section-title i {
  color: var(--color-accent);
  font-size: 1.25rem;
}

.time-filter {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
}

.filter-select {
  padding: var(--spacing-sm) var(--spacing-md);
  border: 1px solid var(--color-border-primary);
  border-radius: var(--radius-sm);
  background: var(--color-bg-tertiary);
  color: var(--color-text-primary);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all var(--transition-normal);
  outline: none;
}

.filter-select:focus {
  border-color: var(--color-accent);
  background: var(--color-bg-primary);
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
}

.pipeline-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--spacing-lg);
}

.pipeline-card {
  background: var(--color-bg-primary);
  border-radius: var(--radius-lg);
  padding: var(--spacing-xl);
  box-shadow: var(--shadow-lg);
  border: 1px solid var(--color-border-tertiary);
  cursor: pointer;
  transition: all var(--transition-normal);
  position: relative;
  overflow: hidden;
  min-height: 140px;
  display: flex;
  align-items: center;
  gap: var(--spacing-lg);
}

.pipeline-card:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-xl);
}

.pipeline-card.active {
  border-color: var(--color-accent);
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1), var(--shadow-lg);
}

.pipeline-icon {
  width: 3rem;
  height: 3rem;
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  color: white;
  flex-shrink: 0;
  box-shadow: var(--shadow-sm);
}

.pipeline-card.new-lead .pipeline-icon {
  background: linear-gradient(135deg, var(--color-info), #2563eb);
}

.pipeline-card.call-scheduled .pipeline-icon {
  background: linear-gradient(135deg, var(--color-accent), #7c3aed);
}

.pipeline-card.quote-received .pipeline-icon {
  background: linear-gradient(135deg, var(--color-warning), #d97706);
}

.pipeline-card.deal-closed .pipeline-icon {
  background: linear-gradient(135deg, var(--color-success), #059669);
}

.pipeline-card.followup .pipeline-icon {
  background: linear-gradient(135deg, var(--color-secondary), #4b5563);
}

.pipeline-content {
  flex: 1;
}

.pipeline-title {
  font-size: 0.875rem;
  color: var(--color-text-secondary);
  margin: 0 0 var(--spacing-xs) 0;
  font-weight: 600;
  line-height: 1.2;
}

.pipeline-value {
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--color-text-primary);
  margin: var(--spacing-xs) 0;
  line-height: 1.2;
}

.pipeline-change {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  font-size: 0.75rem;
  font-weight: 600;
}

.pipeline-change.positive {
  color: var(--color-success);
}

.pipeline-change.negative {
  color: var(--color-error);
}

.pipeline-change.neutral {
  color: var(--color-text-tertiary);
}

/* Leads Section */
.leads-section {
  background: var(--color-bg-primary);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  border: 1px solid var(--color-border-tertiary);
  overflow: hidden;
  max-width: 1400px;
  margin: 0 auto;
}

.leads-section .section-header {
  background: transparent;
  border-radius: 0;
  box-shadow: none;
  border: none;
  border-bottom: 1px solid var(--color-border-tertiary);
  margin-bottom: 0;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.header-left {
  flex: 1;
}

.header-right {
  display: flex;
  align-items: center;
  gap: var(--spacing-lg);
}

.search-container {
  position: relative;
  width: 280px;
}

.search-icon {
  position: absolute;
  right: var(--spacing-md);
  top: 50%;
  transform: translateY(-50%);
  color: var(--color-text-quaternary);
  font-size: 1rem;
}

.search-input {
  width: 100%;
  padding: var(--spacing-sm) 2.5rem var(--spacing-sm) var(--spacing-md);
  border: 1px solid var(--color-border-primary);
  border-radius: var(--radius-md);
  font-size: 0.875rem;
  background: var(--color-bg-tertiary);
  transition: all var(--transition-normal);
  outline: none;
}

.search-input:focus {
  border-color: var(--color-accent);
  background: var(--color-bg-primary);
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
}

.search-input::placeholder {
  color: var(--color-text-quaternary);
}

.filter-controls {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
}

.filter-btn {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  border: 1px solid var(--color-border-secondary);
  background: var(--color-bg-primary);
  color: var(--color-text-secondary);
  border-radius: var(--radius-sm);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all var(--transition-normal);
}

.filter-btn:hover:not(:disabled) {
  background: var(--color-bg-tertiary);
  transform: translateY(-1px);
  box-shadow: var(--shadow-sm);
}

/* Filters Panel */
.filters-panel {
  padding: var(--spacing-xl);
  border-bottom: 1px solid var(--color-border-tertiary);
  background: var(--color-bg-tertiary);
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--spacing-lg);
  align-items: end;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.filter-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--color-text-secondary);
}

.clear-filters-btn {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  border: 1px solid var(--color-border-secondary);
  background: var(--color-bg-primary);
  color: var(--color-text-secondary);
  border-radius: var(--radius-sm);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all var(--transition-normal);
  height: fit-content;
}

.clear-filters-btn:hover:not(:disabled) {
  background: var(--color-bg-tertiary);
  transform: translateY(-1px);
  box-shadow: var(--shadow-sm);
}

/* Loading and Empty States */
.loading-section,
.empty-section {
  padding: var(--spacing-3xl) var(--spacing-2xl);
  text-align: center;
}

.loading-content,
.empty-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-lg);
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--color-bg-tertiary);
  border-top: 3px solid var(--color-accent);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-text {
  color: var(--color-text-tertiary);
  font-size: 1rem;
  font-weight: 500;
}

.empty-icon {
  font-size: 3rem;
  color: var(--color-text-quaternary);
  margin-bottom: var(--spacing-md);
}

.empty-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--color-text-secondary);
  margin: 0 0 var(--spacing-sm) 0;
}

.empty-description {
  color: var(--color-text-tertiary);
  font-size: 0.875rem;
  margin: 0 0 var(--spacing-xl) 0;
  line-height: 1.5;
}

/* Table */
.leads-table-container {
  padding: var(--spacing-2xl);
}

.table-header-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-lg);
}

.bulk-actions {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
}

.selected-count {
  font-size: 0.875rem;
  color: var(--color-text-secondary);
  font-weight: 500;
}

.bulk-btn {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-xs) var(--spacing-sm);
  border: none;
  background: transparent;
  color: var(--color-text-secondary);
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  border-radius: var(--radius-sm);
  transition: all var(--transition-normal);
}

.bulk-btn.delete {
  color: var(--color-error);
}

.bulk-btn.delete:hover {
  background: rgba(239, 68, 68, 0.1);
}

.bulk-btn.export {
  color: var(--color-info);
}

.bulk-btn.export:hover {
  background: rgba(59, 130, 246, 0.1);
}

.table-info {
  font-size: 0.875rem;
  color: var(--color-text-tertiary);
}

.table-wrapper {
  overflow-x: auto;
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border-tertiary);
}

.leads-table {
  width: 100%;
  border-collapse: collapse;
  background: var(--color-bg-primary);
}

.table-header {
  background: var(--color-bg-tertiary);
  padding: var(--spacing-md) var(--spacing-lg);
  text-align: right;
  font-weight: 600;
  font-size: 0.875rem;
  color: var(--color-text-secondary);
  padding: var(--spacing-lg) var(--spacing-md);
  text-align: left;
  border-bottom: 1px solid var(--color-border-primary);
}

.table-row {
  background: var(--color-bg-primary);
  cursor: pointer;
  transition: all var(--transition-normal);
  border-bottom: 1px solid var(--color-border-tertiary);
}

.table-row:hover {
  background: var(--color-bg-tertiary);
}

.table-cell {
  padding: var(--spacing-md);
  text-align: left;
  vertical-align: middle;
}

.checkbox-col {
  width: 2rem;
}

.checkbox {
  width: 1rem;
  height: 1rem;
  border-radius: var(--radius-xs);
  border: 1px solid var(--color-border-secondary);
  cursor: pointer;
}

.company-name {
  font-size: 0.875rem;
  color: var(--color-text-secondary);
  font-weight: 500;
}

.status-badge {
  padding: var(--spacing-xs) var(--spacing-md);
  border-radius: var(--radius-sm);
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  white-space: nowrap;
}

.status-badge.status-new {
  background: rgba(59, 130, 246, 0.1);
  color: var(--color-info);
}

.status-badge.status-scheduled {
  background: rgba(139, 92, 246, 0.1);
  color: var(--color-accent);
}

.status-badge.status-quote {
  background: rgba(245, 158, 11, 0.1);
  color: var(--color-warning);
}

.status-badge.status-closed {
  background: rgba(16, 185, 129, 0.1);
  color: var(--color-success);
}

.status-badge.status-irrelevant {
  background: rgba(239, 68, 68, 0.1);
  color: var(--color-error);
}

.lead-info {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
}

.lead-avatar {
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
}

.avatar-image {
  width: 3rem;
  height: 3rem;
  border-radius: var(--radius-md);
  object-fit: cover;
  border: 2px solid var(--color-bg-primary);
  box-shadow: var(--shadow-sm);
}

.status-indicator {
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid var(--color-bg-primary);
}

.status-indicator.status-new {
  background: var(--color-info);
}

.status-indicator.status-scheduled {
  background: var(--color-accent);
}

.status-indicator.status-quote {
  background: var(--color-warning);
}

.status-indicator.status-closed {
  background: var(--color-success);
}

.status-indicator.status-irrelevant {
  background: var(--color-error);
}

.lead-details {
  flex: 1;
  min-width: 0;
}

.lead-name {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0 0 var(--spacing-xs) 0;
  line-height: 1.3;
}

.lead-id {
  font-size: 0.875rem;
  color: var(--color-text-tertiary);
  margin: 0;
}

.contact-info {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.contact-item {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  font-size: 0.875rem;
  color: var(--color-text-secondary);
}

.contact-item i {
  color: var(--color-text-quaternary);
  font-size: 0.875rem;
  width: 1rem;
  text-align: center;
}

.source-badge {
  padding: var(--spacing-xs) var(--spacing-md);
  border-radius: var(--radius-sm);
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  white-space: nowrap;
}

.date-info {
  font-size: 0.875rem;
  color: var(--color-text-quaternary);
}

.table-actions {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.action-icon {
  width: 2rem;
  height: 2rem;
  border: none;
  border-radius: var(--radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all var(--transition-normal);
  font-size: 0.875rem;
  background: transparent;
}

.action-icon.edit {
  color: var(--color-info);
}

.action-icon.edit:hover {
  background: rgba(59, 130, 246, 0.1);
  transform: scale(1.1);
}

.action-icon.delete {
  color: var(--color-error);
}

.action-icon.delete:hover {
  background: rgba(239, 68, 68, 0.1);
  transform: scale(1.1);
}

.action-icon.more {
  color: var(--color-text-secondary);
}

.action-icon.more:hover {
  background: rgba(139, 92, 246, 0.1);
  transform: scale(1.1);
}

/* Pagination */
.pagination-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--spacing-xl) var(--spacing-2xl);
  border-top: 1px solid var(--color-border-tertiary);
  background: var(--color-bg-tertiary);
}

.pagination-info {
  font-size: 0.875rem;
  color: var(--color-text-tertiary);
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
}

.pagination-btn {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  border: 1px solid var(--color-border-secondary);
  background: var(--color-bg-primary);
  color: var(--color-text-secondary);
  border-radius: var(--radius-sm);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all var(--transition-normal);
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-btn:hover:not(:disabled) {
  background: var(--color-bg-tertiary);
  transform: translateY(-1px);
  box-shadow: var(--shadow-sm);
}

.page-numbers {
  display: flex;
  gap: var(--spacing-xs);
}

.page-btn {
  width: 2.5rem;
  height: 2.5rem;
  border: 1px solid var(--color-border-secondary);
  background: var(--color-bg-primary);
  color: var(--color-text-secondary);
  border-radius: var(--radius-sm);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all var(--transition-normal);
  display: flex;
  align-items: center;
  justify-content: center;
}

.page-btn:hover {
  background: var(--color-bg-tertiary);
  transform: translateY(-1px);
  box-shadow: var(--shadow-sm);
}

.page-btn.active {
  background: var(--color-accent);
  color: white;
  border-color: var(--color-accent);
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: var(--spacing-lg);
}

.modal-container {
  background: var(--color-bg-primary);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-xl);
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: var(--spacing-2xl) var(--spacing-2xl) var(--spacing-xl);
  border-bottom: 1px solid var(--color-border-tertiary);
}

.modal-title-section {
  flex: 1;
}

.modal-title {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0 0 var(--spacing-xs) 0;
}

.modal-title i {
  color: var(--color-accent);
}

.modal-subtitle {
  color: var(--color-text-tertiary);
  font-size: 0.875rem;
  margin: 0;
}

.modal-close {
  width: 2.5rem;
  height: 2.5rem;
  border: none;
  background: var(--color-bg-tertiary);
  color: var(--color-text-secondary);
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: all var(--transition-normal);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
}

.modal-close:hover {
  background: var(--color-bg-quaternary);
  transform: scale(1.1);
}

.modal-form {
  padding: var(--spacing-2xl);
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--spacing-lg);
  margin-bottom: var(--spacing-xl);
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.form-group.full-width {
  grid-column: 1 / -1;
}

.form-label {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--color-text-secondary);
}

.form-label i {
  color: var(--color-text-quaternary);
  font-size: 0.875rem;
}

.form-input,
.form-select,
.form-textarea {
  padding: var(--spacing-md);
  border: 1px solid var(--color-border-primary);
  border-radius: var(--radius-sm);
  background: var(--color-bg-tertiary);
  color: var(--color-text-primary);
  font-size: 0.875rem;
  transition: all var(--transition-normal);
  outline: none;
}

.form-input:focus,
.form-select:focus,
.form-textarea:focus {
  border-color: var(--color-accent);
  background: var(--color-bg-primary);
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
}

.form-textarea {
  resize: vertical;
  min-height: 100px;
  font-family: inherit;
  line-height: 1.5;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--spacing-md);
  padding-top: var(--spacing-xl);
  border-top: 1px solid var(--color-border-tertiary);
}

/* Animations */
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.spin {
  animation: spin 1s linear infinite;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .dashboard-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .leads-section .section-header {
    flex-direction: column;
    gap: var(--spacing-lg);
    align-items: stretch;
  }
}

@media (max-width: 1024px) {
  .leads-dashboard {
    padding: var(--spacing-lg);
  }
  
  .header-content {
    flex-direction: column;
    gap: var(--spacing-xl);
    align-items: stretch;
  }
  
  .header-actions {
    justify-content: space-between;
  }
  
  .search-container {
    width: 100%;
    max-width: 300px;
  }
}

@media (max-width: 768px) {
  .leads-dashboard {
    padding: var(--spacing-md);
  }
  
  .header-content {
    padding: var(--spacing-xl);
  }
  
  .dashboard-grid {
    grid-template-columns: 1fr;
  }
  
  .pipeline-cards {
    grid-template-columns: 1fr;
  }
  
  .tabs-container {
    flex-direction: column;
    gap: var(--spacing-xs);
  }
  
  .tab-btn {
    justify-content: center;
    padding: var(--spacing-md);
  }
  
  .leads-section .section-header {
    flex-direction: column;
    gap: var(--spacing-lg);
    align-items: stretch;
  }
  
  .pagination-section {
    flex-direction: column;
    gap: var(--spacing-lg);
    align-items: stretch;
  }
  
  .pagination-controls {
    justify-content: center;
  }
  
  .form-grid {
    grid-template-columns: 1fr;
  }
  
  .modal-actions {
    flex-direction: column-reverse;
  }
  
  .action-btn {
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .leads-dashboard {
    padding: var(--spacing-sm);
  }
  
  .header-content {
    padding: var(--spacing-lg);
  }
  
  .page-title {
    font-size: 1.5rem;
  }
  
  .header-actions {
    flex-direction: column;
    gap: var(--spacing-md);
  }
  
  .search-container {
    width: 100%;
  }
  
  .section-header {
    padding: var(--spacing-lg);
  }
  
  .section-title {
    font-size: 1.25rem;
  }
  
  .leads-section .section-header {
    padding: var(--spacing-lg);
  }
  
  .lead-card {
    margin-bottom: var(--spacing-md);
  }
  
  .page-numbers {
    display: none;
  }
  
  .tabs-container {
    padding: var(--spacing-sm);
  }
  
  .tab-btn {
    padding: var(--spacing-sm) var(--spacing-md);
    font-size: 0.8rem;
  }
}

/* Focus Management */
.action-btn:focus,
.form-input:focus,
.form-select:focus,
.form-textarea:focus,
.filter-select:focus,
.search-input:focus {
  outline: 2px solid var(--color-accent);
  outline-offset: 2px;
}

/* Print Styles */
@media print {
  .leads-dashboard {
    background: white;
    padding: 0;
  }
  
  .header-actions,
  .lead-actions,
  .modal-overlay {
    display: none;
  }
  
  .dashboard-card,
  .lead-card {
    box-shadow: none;
    border: 1px solid #ccc;
  }
}

/* Tabs Section */
.tabs-section {
  margin-bottom: var(--spacing-2xl);
  max-width: 1400px;
  margin-left: auto;
  margin-right: auto;
}

.tabs-container {
  background: var(--color-bg-primary);
  border-radius: var(--radius-lg);
  padding: var(--spacing-md);
  box-shadow: var(--shadow-lg);
  border: 1px solid var(--color-border-tertiary);
  display: flex;
  gap: var(--spacing-sm);
}

.tab-btn {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-md) var(--spacing-xl);
  border: none;
  border-radius: var(--radius-md);
  background: transparent;
  color: var(--color-text-secondary);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all var(--transition-normal);
  white-space: nowrap;
}

.tab-btn:hover {
  background: var(--color-bg-tertiary);
  color: var(--color-text-primary);
}

.tab-btn.active {
  background: linear-gradient(135deg, var(--color-accent), #7c3aed);
  color: white;
  box-shadow: var(--shadow-sm);
}

.tab-btn.active:hover {
  background: linear-gradient(135deg, #7c3aed, var(--color-accent));
}

.tab-btn i {
  font-size: 1rem;
}

.tab-badge {
  background: var(--color-bg-quaternary);
  color: var(--color-text-secondary);
  font-size: 0.75rem;
  font-weight: 600;
  padding: var(--spacing-xs) var(--spacing-sm);
  border-radius: var(--radius-sm);
  min-width: 1.5rem;
  text-align: center;
  transition: all var(--transition-normal);
}

.tab-btn.active .tab-badge {
  background: rgba(255, 255, 255, 0.2);
  color: white;
}

/* Tab Content */
.tab-content {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Advanced Analytics Section */
.analytics-section {
  margin-bottom: var(--spacing-2xl);
  max-width: 1400px;
  margin-left: auto;
  margin-right: auto;
  background: var(--color-bg-primary);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  border: 1px solid var(--color-border-tertiary);
  overflow: hidden;
}

.analytics-section .section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--spacing-2xl);
  border-bottom: 1px solid var(--color-border-tertiary);
  background: linear-gradient(135deg, 
    rgba(139, 92, 246, 0.05), 
    rgba(59, 130, 246, 0.05)
  );
}

.analytics-controls {
  display: flex;
  gap: var(--spacing-xs);
  background: var(--color-bg-tertiary);
  padding: var(--spacing-xs);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border-tertiary);
}

.analytics-btn {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-lg);
  border: none;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--color-text-secondary);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all var(--transition-normal);
  white-space: nowrap;
}

.analytics-btn:hover {
  background: var(--color-bg-primary);
  color: var(--color-text-primary);
  transform: translateY(-1px);
}

.analytics-btn.active {
  background: linear-gradient(135deg, var(--color-accent), #7c3aed);
  color: white;
  box-shadow: var(--shadow-sm);
}

.analytics-btn.active:hover {
  background: linear-gradient(135deg, #7c3aed, var(--color-accent));
  transform: translateY(-1px);
}

.analytics-btn i {
  font-size: 1rem;
}

.analytics-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-2xl);
  padding: var(--spacing-2xl);
  min-height: 400px;
}

.analytics-chart {
  background: linear-gradient(135deg, 
    var(--color-bg-secondary), 
    rgba(139, 92, 246, 0.02)
  );
  border-radius: var(--radius-lg);
  border: 1px solid var(--color-border-secondary);
  position: relative;
  overflow: hidden;
  transition: all var(--transition-normal);
}

.analytics-chart::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at 30% 30%, 
    rgba(139, 92, 246, 0.1), 
    transparent 50%
  );
  pointer-events: none;
}

.analytics-chart:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-xl);
  border-color: rgba(139, 92, 246, 0.3);
}

.pie-chart-container,
.bar-chart-container {
  padding: var(--spacing-2xl);
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 2;
}

.pie-chart,
.bar-chart {
  width: 100%;
  height: 100%;
  max-width: 280px;
  max-height: 280px;
}

.pie-segment {
  cursor: pointer;
  transition: all var(--transition-normal);
  transform-origin: center;
}

.pie-segment:hover {
  transform: scale(1.05);
  filter: brightness(1.1);
}

.pie-segment.active {
  transform: scale(1.08);
  filter: brightness(1.2) drop-shadow(0 0 10px rgba(139, 92, 246, 0.4));
}

.pie-center-title {
  font-size: 0.875rem;
  font-weight: 600;
  fill: var(--color-text-secondary);
  text-anchor: middle;
}

.pie-center-value {
  font-size: 2rem;
  font-weight: 800;
  fill: var(--color-text-primary);
  text-anchor: middle;
}

.bar-segment {
  cursor: pointer;
  transition: all var(--transition-normal);
}

.bar-segment:hover {
  filter: brightness(1.2);
  transform: scaleY(1.05);
  transform-origin: bottom;
}

.bar-value {
  font-size: 0.875rem;
  font-weight: 600;
  fill: var(--color-text-primary);
  text-anchor: middle;
  opacity: 0;
  animation: fadeInUp 0.5s ease-out 1.2s forwards;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.grid-lines {
  opacity: 0.3;
}

.analytics-legend {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
  padding: var(--spacing-xl);
  background: var(--color-bg-secondary);
  border-radius: var(--radius-lg);
  border: 1px solid var(--color-border-secondary);
}

.legend-item {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-md);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all var(--transition-normal);
  border: 1px solid transparent;
}

.legend-item:hover {
  background: var(--color-bg-tertiary);
  border-color: var(--color-border-secondary);
  transform: translateX(4px);
}

.legend-item.active {
  background: linear-gradient(135deg, 
    rgba(139, 92, 246, 0.1), 
    rgba(59, 130, 246, 0.05)
  );
  border-color: var(--color-accent);
  transform: translateX(6px);
  box-shadow: var(--shadow-md);
}

.legend-color {
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 50%;
  position: relative;
  box-shadow: var(--shadow-sm);
}

.legend-color::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.3);
  transform: translate(-50%, -50%);
  transition: all var(--transition-normal);
}

.legend-item:hover .legend-color::before,
.legend-item.active .legend-color::before {
  width: 0.75rem;
  height: 0.75rem;
}

.legend-color.new-lead {
  background: linear-gradient(135deg, #60a5fa, #3b82f6);
}

.legend-color.call-scheduled {
  background: linear-gradient(135deg, #a78bfa, #8b5cf6);
}

.legend-color.quote-received {
  background: linear-gradient(135deg, #fbbf24, #f59e0b);
}

.legend-color.deal-closed {
  background: linear-gradient(135deg, #4ade80, #22c55e);
}

.legend-color.followup {
  background: linear-gradient(135deg, #fb7185, #f43f5e);
}

.legend-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.legend-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--color-text-secondary);
}

.legend-value {
  font-size: 1rem;
  font-weight: 700;
  color: var(--color-text-primary);
}

/* Responsive Design for Analytics */
@media (max-width: 1024px) {
  .analytics-container {
    grid-template-columns: 1fr;
    gap: var(--spacing-xl);
  }
  
  .analytics-chart {
    min-height: 300px;
  }
}

@media (max-width: 768px) {
  .analytics-section .section-header {
    flex-direction: column;
    gap: var(--spacing-lg);
    align-items: stretch;
  }
  
  .analytics-controls {
    justify-content: center;
  }
  
  .analytics-container {
    padding: var(--spacing-lg);
  }
  
  .pie-chart-container,
  .bar-chart-container {
    padding: var(--spacing-lg);
  }
  
  .legend-item {
    padding: var(--spacing-sm);
  }
  
  .legend-label,
  .legend-value {
    font-size: 0.8rem;
  }
}

/* Final Chart Animation Enhancements */

/* Chart loading animation */
@keyframes chartLoad {
  0% {
    opacity: 0;
    transform: scale(0.9) translateY(10px);
  }
  100% {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.card-chart {
  animation: chartLoad 0.6s ease-out forwards;
}

.analytics-chart {
  animation: chartLoad 0.8s ease-out forwards;
}

/* Pulse animation for important elements */
@keyframes subtlePulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.9;
    transform: scale(1.02);
  }
}

.dashboard-card.active .mini-chart,
.pie-segment.active,
.legend-item.active .legend-color {
  animation: subtlePulse 2s ease-in-out infinite;
}

/* Gradient shimmer effect */
@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

.dashboard-card::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.03),
    transparent
  );
  background-size: 200% 100%;
  animation: shimmer 3s ease-in-out infinite;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.dashboard-card:hover::after {
  opacity: 1;
}

/* Enhanced glow effects */
.dashboard-card.total-leads:hover .mini-chart {
  filter: brightness(1.05) drop-shadow(0 0 20px rgba(59, 130, 246, 0.3));
}

.dashboard-card.hot-leads:hover .mini-chart {
  filter: brightness(1.05) drop-shadow(0 0 20px rgba(239, 68, 68, 0.3));
}

.dashboard-card.conversions:hover .mini-chart {
  filter: brightness(1.05) drop-shadow(0 0 20px rgba(34, 197, 94, 0.3));
}

.dashboard-card.revenue:hover .mini-chart {
  filter: brightness(1.05) drop-shadow(0 0 20px rgba(168, 85, 247, 0.3));
}

/* Smooth transitions for all interactive elements */
.pie-segment,
.bar-segment,
.legend-item,
.data-dot,
.flame-dot,
.success-dot,
.revenue-dot {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Loading skeleton for charts */
@keyframes skeleton {
  0%, 100% {
    opacity: 0.3;
  }
  50% {
    opacity: 0.7;
  }
}

.chart-loading {
  animation: skeleton 1.5s ease-in-out infinite;
}

/* Enhanced focus states for accessibility */
.pie-segment:focus,
.bar-segment:focus,
.legend-item:focus {
  outline: 3px solid var(--color-accent);
  outline-offset: 2px;
}

/* Responsive adjustments for animations */
@media (max-width: 768px) {
  .dashboard-card::after {
    display: none;
  }
  
  .dashboard-card:hover .mini-chart {
    transform: scale(1.01);
  }
}

/* Reduced motion preferences */
@media (prefers-reduced-motion: reduce) {
  .card-chart,
  .analytics-chart {
    animation: none;
  }
  
  .dashboard-card::after {
    display: none;
  }
  
  .pie-segment.active,
  .legend-item.active .legend-color {
    animation: none;
  }
  
  .chart-path,
  .chart-area,
  .pie-segment,
  .bar-segment {
    transition: none;
  }
}

/* Enhanced Styles for New Dashboard Cards */

/* Revenue Growth Card Specific Styles */
.dashboard-card.revenue-growth .card-icon {
  background: linear-gradient(135deg, #06d6a0, #118c4f);
  box-shadow: 0 8px 20px rgba(6, 214, 160, 0.3);
}

.dashboard-card.revenue-growth:hover {
  background: radial-gradient(circle at top right, rgba(6, 214, 160, 0.08), transparent);
  box-shadow: 
    0 25px 50px -12px rgba(6, 214, 160, 0.25),
    0 0 0 1px rgba(6, 214, 160, 0.1);
}

.dashboard-card.revenue-growth .card-chart {
  background: linear-gradient(135deg, 
    rgba(6, 214, 160, 0.05), 
    rgba(17, 140, 79, 0.02)
  );
  border: 1px solid rgba(6, 214, 160, 0.15);
}

.dashboard-card.revenue-growth:hover .card-chart {
  background: linear-gradient(135deg, 
    rgba(6, 214, 160, 0.1), 
    rgba(17, 140, 79, 0.05)
  );
  border-color: rgba(6, 214, 160, 0.25);
  box-shadow: 
    inset 0 1px 0 rgba(255, 255, 255, 0.1),
    0 8px 16px rgba(6, 214, 160, 0.15);
}

/* Lead Conversion Card Specific Styles */
.dashboard-card.lead-conversion .card-icon {
  background: linear-gradient(135deg, #ff6b6b, #32cd32);
  box-shadow: 0 8px 20px rgba(255, 107, 107, 0.3);
}

.dashboard-card.lead-conversion:hover {
  background: radial-gradient(circle at top right, rgba(50, 205, 50, 0.08), transparent);
  box-shadow: 
    0 25px 50px -12px rgba(50, 205, 50, 0.25),
    0 0 0 1px rgba(50, 205, 50, 0.1);
}

.dashboard-card.lead-conversion .card-chart {
  background: linear-gradient(135deg, 
    rgba(255, 107, 107, 0.05), 
    rgba(50, 205, 50, 0.05)
  );
  border: 1px solid rgba(50, 205, 50, 0.15);
}

.dashboard-card.lead-conversion:hover .card-chart {
  background: linear-gradient(135deg, 
    rgba(255, 107, 107, 0.08), 
    rgba(50, 205, 50, 0.1)
  );
  border-color: rgba(50, 205, 50, 0.25);
  box-shadow: 
    inset 0 1px 0 rgba(255, 255, 255, 0.1),
    0 8px 16px rgba(50, 205, 50, 0.15);
}

/* Total Orders Card Specific Styles */
.dashboard-card.total-orders .card-icon {
  background: linear-gradient(135deg, #667eea, #764ba2);
  box-shadow: 0 8px 20px rgba(102, 126, 234, 0.3);
}

.dashboard-card.total-orders:hover {
  background: radial-gradient(circle at top right, rgba(102, 126, 234, 0.08), transparent);
  box-shadow: 
    0 25px 50px -12px rgba(102, 126, 234, 0.25),
    0 0 0 1px rgba(102, 126, 234, 0.1);
}

.dashboard-card.total-orders .card-chart {
  background: linear-gradient(135deg, 
    rgba(102, 126, 234, 0.05), 
    rgba(118, 75, 162, 0.05)
  );
  border: 1px solid rgba(102, 126, 234, 0.15);
}

.dashboard-card.total-orders:hover .card-chart {
  background: linear-gradient(135deg, 
    rgba(102, 126, 234, 0.1), 
    rgba(118, 75, 162, 0.08)
  );
  border-color: rgba(102, 126, 234, 0.25);
  box-shadow: 
    inset 0 1px 0 rgba(255, 255, 255, 0.1),
    0 8px 16px rgba(102, 126, 234, 0.15);
}

/* Annual Profit Card Specific Styles */
.dashboard-card.annual-profit .card-icon {
  background: linear-gradient(135deg, #ffd700, #ff8c00);
  box-shadow: 0 8px 20px rgba(255, 215, 0, 0.4);
}

.dashboard-card.annual-profit:hover {
  background: radial-gradient(circle at top right, rgba(255, 215, 0, 0.08), transparent);
  box-shadow: 
    0 25px 50px -12px rgba(255, 215, 0, 0.3),
    0 0 0 1px rgba(255, 215, 0, 0.15);
}

.dashboard-card.annual-profit .card-chart {
  background: linear-gradient(135deg, 
    rgba(255, 215, 0, 0.08), 
    rgba(255, 140, 0, 0.05)
  );
  border: 1px solid rgba(255, 215, 0, 0.2);
}

.dashboard-card.annual-profit:hover .card-chart {
  background: linear-gradient(135deg, 
    rgba(255, 215, 0, 0.15), 
    rgba(255, 140, 0, 0.1)
  );
  border-color: rgba(255, 215, 0, 0.3);
  box-shadow: 
    inset 0 1px 0 rgba(255, 255, 255, 0.2),
    0 8px 16px rgba(255, 215, 0, 0.2);
}

/* Enhanced Chart Element Styles */
.growth-dot,
.conversion-dot,
.order-bar,
.profit-dot {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
}

.growth-dot:hover,
.conversion-dot:hover,
.profit-dot:hover {
  transform: scale(1.3);
  filter: brightness(1.2) drop-shadow(0 0 8px currentColor);
}

.order-bar:hover {
  transform: scaleY(1.1);
  transform-origin: bottom;
  filter: brightness(1.2);
}

/* Funnel Step Styles */
.funnel-step {
  transition: all 0.3s ease;
  cursor: pointer;
}

.funnel-step:hover {
  transform: scale(1.05);
  filter: brightness(1.1);
}

/* Advanced Animations for New Cards */
@keyframes growthPulse {
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.05); opacity: 0.8; }
}

@keyframes conversionFlow {
  0% { stroke-dashoffset: 300; }
  100% { stroke-dashoffset: 0; }
}

@keyframes ordersRise {
  0% { transform: scaleY(0); opacity: 0; }
  100% { transform: scaleY(1); opacity: 1; }
}

@keyframes profitShine {
  0% { filter: brightness(1); }
  50% { filter: brightness(1.3) drop-shadow(0 0 10px #ffd700); }
  100% { filter: brightness(1); }
}

.dashboard-card.revenue-growth:hover .growth-dot {
  animation: growthPulse 2s ease-in-out infinite;
}

.dashboard-card.lead-conversion:hover .conversion-line {
  animation: conversionFlow 2s ease-in-out infinite;
}

.dashboard-card.total-orders:hover .order-bar {
  animation: ordersRise 0.8s ease-out;
}

.dashboard-card.annual-profit:hover .profit-dot {
  animation: profitShine 1.5s ease-in-out infinite;
}

/* Enhanced Value Display */
.dashboard-card.revenue-growth .card-value {
  background: linear-gradient(135deg, #06d6a0, #118c4f);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 900;
  text-shadow: 0 2px 4px rgba(6, 214, 160, 0.3);
}

.dashboard-card.lead-conversion .card-value {
  background: linear-gradient(135deg, #32cd32, #228b22);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 900;
  text-shadow: 0 2px 4px rgba(50, 205, 50, 0.3);
}

.dashboard-card.total-orders .card-value {
  background: linear-gradient(135deg, #667eea, #764ba2);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 900;
  text-shadow: 0 2px 4px rgba(102, 126, 234, 0.3);
}

.dashboard-card.annual-profit .card-value {
  background: linear-gradient(135deg, #ffd700, #ff8c00);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 900;
  text-shadow: 0 2px 4px rgba(255, 215, 0, 0.4);
}

/* Interactive Card Headers */
.dashboard-card.revenue-growth .card-header::before,
.dashboard-card.lead-conversion .card-header::before,
.dashboard-card.total-orders .card-header::before,
.dashboard-card.annual-profit .card-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, 
    var(--card-primary-color, #667eea), 
    var(--card-secondary-color, #764ba2)
  );
  border-radius: var(--radius-lg) var(--radius-lg) 0 0;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.dashboard-card.revenue-growth {
  --card-primary-color: #06d6a0;
  --card-secondary-color: #118c4f;
}

.dashboard-card.lead-conversion {
  --card-primary-color: #ff6b6b;
  --card-secondary-color: #32cd32;
}

.dashboard-card.total-orders {
  --card-primary-color: #667eea;
  --card-secondary-color: #764ba2;
}

.dashboard-card.annual-profit {
  --card-primary-color: #ffd700;
  --card-secondary-color: #ff8c00;
}

.dashboard-card:hover .card-header::before {
  opacity: 1;
}

/* Card Loading State Animation */
@keyframes cardLoadSlideUp {
  0% {
    opacity: 0;
    transform: translateY(30px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

.dashboard-card.revenue-growth {
  animation: cardLoadSlideUp 0.6s ease-out 0.1s both;
}

.dashboard-card.lead-conversion {
  animation: cardLoadSlideUp 0.6s ease-out 0.2s both;
}

.dashboard-card.total-orders {
  animation: cardLoadSlideUp 0.6s ease-out 0.3s both;
}

.dashboard-card.annual-profit {
  animation: cardLoadSlideUp 0.6s ease-out 0.4s both;
}

/* Chart Element Enhancements */
.growth-line,
.conversion-line,
.orders-line,
.profit-line {
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
}

.chart-area {
  transition: all 0.3s ease;
}

.dashboard-card:hover .chart-area {
  opacity: 0.8 !important;
}

/* Responsive Enhancements */
@media (max-width: 768px) {
  .dashboard-card.revenue-growth .card-value,
  .dashboard-card.lead-conversion .card-value,
  .dashboard-card.total-orders .card-value,
  .dashboard-card.annual-profit .card-value {
    font-size: 2rem;
  }
}

/* Dark mode support */
@media (prefers-color-scheme: dark) {
  .dashboard-card.revenue-growth,
  .dashboard-card.lead-conversion,
  .dashboard-card.total-orders,
  .dashboard-card.annual-profit {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.1);
  }
  
  .dashboard-card:hover {
    background: rgba(255, 255, 255, 0.08);
  }
}
</style> 
